// src/components/NascarPracticePage.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

/* ------------------------ data fetch hook ------------------------ */
function useJson(url) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(url, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => {
        if (alive) {
          setData(j);
          setErr(null);
        }
      })
      .catch((e) => {
        if (alive) {
          setErr(e);
          setData(null);
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [url]);

  return { data, err, loading };
}

/* ---------------- LAST UPDATED (shared) ---------------- */
function useLastUpdated(mainUrl, metaUrl) {
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const h = await fetch(mainUrl, { method: "HEAD", cache: "no-store" });
        const lm = h.headers.get("last-modified");
        if (alive && lm) {
          setUpdatedAt(new Date(lm));
          return;
        }
      } catch (_) {}

      try {
        const r = await fetch(mainUrl, { cache: "no-store" });
        const lm2 = r.headers.get("last-modified");
        if (alive && lm2) {
          setUpdatedAt(new Date(lm2));
          return;
        }
      } catch (_) {}

      try {
        if (!metaUrl) return;
        const m = await fetch(`${metaUrl}?_=${Date.now()}`, {
          cache: "no-store",
        }).then((x) => x.json());
        const iso =
          m?.updated_iso ||
          m?.updated_utc ||
          m?.updated ||
          m?.lastUpdated ||
          m?.timestamp;
        const ep = m?.updated_epoch;
        const d = iso
          ? new Date(iso)
          : Number.isFinite(ep)
          ? new Date(ep * 1000)
          : null;
        if (alive && d && !isNaN(d)) setUpdatedAt(d);
      } catch (_) {}
    })();
    return () => {
      alive = false;
    };
  }, [mainUrl, metaUrl]);

  return updatedAt;
}

const fmtUpdated = (d) =>
  d
    ? d.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

/* ----------------------------- helpers ----------------------------- */
const num = (v) => {
  if (v === null || v === undefined) return NaN;
  let s = String(v).trim();
  if (s === "" || s === "-" || s === "—" || /^n\/?a$/i.test(s)) return NaN;
  s = s.replace(/,/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

const isNumeric = (v) => Number.isFinite(num(v));

const numericColName = (c) => /^\d+$/.test(String(c).trim());

const bestRankColName = (c) => /^best\s+\d+$/i.test(String(c).trim());

const looksNumericCol = (c) =>
  numericColName(c) ||
  bestRankColName(c) ||
  /(^rank$|^avg|average|laps?$|^std\s*dev$|sal$|qual$|value$|score$|median$|falloff|trend|stability|removed|shift|factor|ratio|band)/i.test(
    String(c).trim()
  );

const cmp = (a, b, dir = "asc") => {
  const aBlank =
    a === null ||
    a === undefined ||
    String(a).trim() === "" ||
    String(a).trim() === "-" ||
    String(a).trim() === "—" ||
    /^n\/?a$/i.test(String(a).trim());

  const bBlank =
    b === null ||
    b === undefined ||
    String(b).trim() === "" ||
    String(b).trim() === "-" ||
    String(b).trim() === "—" ||
    /^n\/?a$/i.test(String(b).trim());

  // Always keep missing values at the bottom, regardless of sort direction.
  if (aBlank && !bBlank) return 1;
  if (!aBlank && bBlank) return -1;
  if (aBlank && bBlank) return 0;

  const na = isNumeric(a);
  const nb = isNumeric(b);

  if (na && nb) {
    const va = num(a);
    const vb = num(b);
    return dir === "asc" ? va - vb : vb - va;
  }

  const sa = String(a).toLowerCase();
  const sb = String(b).toLowerCase();

  if (sa < sb) return dir === "asc" ? -1 : 1;
  if (sa > sb) return dir === "asc" ? 1 : -1;
  return 0;
};

const findDriverKey = (row) =>
  Object.keys(row || {}).find((k) => /^driver\b/i.test(k)) || "Driver_1";

/** Reorder LAP-BY-LAP so the “main block” is first, then 1..N lap columns, then rest. */
function reorderLapCols(cols) {
  const START = ["Rank", "Average_1", "Driver_1", "#of Laps", "Group"];
  const LAP_BLOCKS = [
    ["1 Lap_1", "1 Lap"],
    ["3 Lap_1", "3 Lap"],
    ["5 Lap_1", "5 Lap"],
    ["10 Lap_1", "10 Lap"],
    ["15 Lap_1", "15 Lap"],
    ["20 Lap_1", "20 Lap"],
    ["25 Lap_1", "25 Lap"],
    ["30 Lap_1", "30 Lap"],
    ["Lap Avg", "LapAvg", "Lap Average"],
    ["Sh. Run", "Short Run"],
    ["Lo. Run", "Long Run"],
    ["Average"],
  ];
  const END = ["Std Dev"];
  const pick = (pair) => pair.find((name) => cols.includes(name));
  const mainBlock = [
    ...START.filter((c) => cols.includes(c)),
    ...LAP_BLOCKS.map(pick).filter(Boolean),
    ...END.filter((c) => cols.includes(c)),
  ];
  const numeric = cols
    .filter((c) => /^\d+$/.test(String(c)))
    .map(Number)
    .sort((a, b) => a - b)
    .map(String);
  const rest = cols.filter(
    (c) => !mainBlock.includes(c) && !numeric.includes(c)
  );
  return [...mainBlock, ...numeric, ...rest];
}

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const escapeCSV = (v) => {
  const s = String(v ?? "");
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const isClose = (a, b, eps = 1e-9) =>
  Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) <= eps;

/* Lower = better for these columns */
const lowerIsBetter = (c) => {
  const s = String(c).trim().toLowerCase();
  if (s === "rank") return true;
  if (s === "average_1") return true;
  if (s === "std dev" || s === "std. dev" || s === "stdev") return true;
  if (/^(1|3|5|10|15|20|25|30)\s*lap(_1)?$/.test(s)) return true;
  if (/^lap\s*avg/.test(s)) return true;
  if (/^(sh\.\s*run|short\s*run|lo\.\s*run|long\s*run)$/.test(s)) return true;
  if (s === "average") return true;
  if (bestRankColName(s)) return true;
  if (numericColName(s)) return true;

  // analyzer fields
  if (
    s === "pace median" ||
    s === "late run median" ||
    s === "adj pace median" ||
    s === "adj late run median" ||
    s === "median" ||
    s === "adj median" ||
    s === "pace sd" ||
    s === "pace band" ||
    s === "stability"
  ) {
    return true;
  }

  // lower falloff is better
  if (s === "falloff") return true;

  // more negative / lower trend is generally better for lap times
  if (s === "trend/10") return true;

  // removed %
  if (s === "removed %") return true;

  return false;
};

/* Pretty labels for headers / chips */
const prettyColLabel = (c) => {
  const s = String(c);

  if (/^average_1$/i.test(s)) return "Average";
  if (/^driver_1$/i.test(s)) return "Driver";
  if (/^sh\.\s*run$/i.test(s) || /^short\s*run$/i.test(s)) return "Short Run";
  if (/^lo\.\s*run$/i.test(s) || /^long\s*run$/i.test(s)) return "Long Run";

  const lapMatch = s.match(/^(\d+)\s*lap_1$/i);
  if (lapMatch) return `${lapMatch[1]} Lap`;

  const bestMatch = s.match(/^best\s+(\d+)$/i);
  if (bestMatch) return `Best ${bestMatch[1]}`;

  return s.replace(/_1\b/i, "");
};

/* ----------------------------- Lap Analyzer math ----------------------------- */
const median = (arr) => {
  const a = arr.filter(Number.isFinite).slice().sort((x, y) => x - y);
  const n = a.length;
  if (!n) return NaN;
  const mid = Math.floor(n / 2);
  return n % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
};

const mean = (arr) => {
  const a = arr.filter(Number.isFinite);
  if (!a.length) return NaN;
  let s = 0;
  for (const x of a) s += x;
  return s / a.length;
};

const stddev = (arr) => {
  const a = arr.filter(Number.isFinite);
  const n = a.length;
  if (n < 2) return NaN;
  const m = mean(a);
  let ss = 0;
  for (const x of a) ss += (x - m) * (x - m);
  return Math.sqrt(ss / (n - 1));
};

const percentile = (arr, p) => {
  const a = arr.filter(Number.isFinite).slice().sort((x, y) => x - y);
  const n = a.length;
  if (!n) return NaN;
  const idx = (n - 1) * clamp(p, 0, 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return a[lo];
  const t = idx - lo;
  return a[lo] * (1 - t) + a[hi] * t;
};

const mad = (arr) => {
  const xs = arr.filter(Number.isFinite);
  if (!xs.length) return NaN;
  const med = median(xs);
  return median(xs.map((x) => Math.abs(x - med)));
};

/* NEW: true stability = lap-to-lap smoothness */
const lapToLapDeltaMAD = (arr) => {
  const diffs = [];
  for (let i = 1; i < arr.length; i++) {
    const a = arr[i - 1];
    const b = arr[i];
    if (Number.isFinite(a) && Number.isFinite(b)) {
      diffs.push(Math.abs(b - a));
    }
  }
  return diffs.length >= 3 ? mad(diffs) : NaN;
};

/* ----------------------------- robust heatmap helpers ----------------------------- */

/**
 * Cleans only the VALUES USED TO BUILD THE COLOR SCALE.
 *
 * Important:
 * - This does NOT remove the value from the table.
 * - This does NOT stop a cell from being colored.
 * - This only prevents absurd values from deciding the heatmap range.
 *
 * Example:
 * field mostly 135s, one real-bad 165, one junk 800
 * - 800 still shows full red
 * - 165 also shows very red
 * - normal 135-145 values get a useful spread
 */
const cleanHeatVals = (
  vals,
  {
    pctLo = 0.05,
    pctHi = 0.85,
    madLo = 3.0,
    madHi = 2.5,
    minKeep = 4,
  } = {}
) => {
  const xs = vals.filter(Number.isFinite);
  if (xs.length < minKeep + 2) return xs;

  const med = median(xs);
  const m = mad(xs);
  const sigma = Number.isFinite(m) && m > 1e-9 ? m * 1.4826 : NaN;

  const pLow = percentile(xs, pctLo);
  const pHigh = percentile(xs, pctHi);

  let lo = pLow;
  let hi = pHigh;

  if (Number.isFinite(sigma)) {
    lo = Math.max(lo, med - madLo * sigma);
    hi = Math.min(hi, med + madHi * sigma);
  }

  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) {
    return xs;
  }

  const cleaned = xs.filter((v) => v >= lo && v <= hi);

  // If MAD trimming gets too aggressive, fall back to percentile-only trimming.
  if (cleaned.length < minKeep) {
    const pctOnly = xs.filter((v) => v >= pLow && v <= pHigh);
    return pctOnly.length >= minKeep ? pctOnly : xs;
  }

  return cleaned;
};

/**
 * Builds per-column heatmap stats.
 *
 * Absurd values are excluded from the SCALE, but the displayed cell value still
 * gets styled by getRobustHeatStyle. So a junk 800 stays full red while not
 * ruining the scale for the rest of the column.
 */
function buildRobustHeatStats(rows, cols, opts = {}) {
  const {
    lowerBetterFn = () => true,
    hardCapNumeric = null,
    madMultLo = 2.5,
    madMultHi = 3.5,
    pctLo = 0.1,
    pctHi = 0.9,

    // Scale cleaning options
    cleanScale = true,
    cleanPctLo = 0.05,
    cleanPctHi = 0.85,
    cleanMadLo = 3.0,
    cleanMadHi = 2.5,
  } = opts;

  const stats = {};

  for (const c of cols) {
    if (!lowerBetterFn(c)) continue;

    const rawVals = rows.map((r) => num(r?.[c])).filter(Number.isFinite);
    if (rawVals.length < 4) continue;

    const isLapLike = numericColName(c) || bestRankColName(c);

    let vals = cleanScale
      ? cleanHeatVals(rawVals, {
          pctLo: isLapLike ? cleanPctLo : 0.05,
          pctHi: isLapLike ? cleanPctHi : 0.9,
          madLo: isLapLike ? cleanMadLo : 3.0,
          madHi: isLapLike ? cleanMadHi : 3.0,
          minKeep: 4,
        })
      : rawVals;

    if (vals.length < 4) continue;

    const med = median(vals);
    const m = mad(vals);
    const pLo = percentile(vals, pctLo);
    const pHi = percentile(vals, pctHi);

    let lo;
    let hi;

    if (Number.isFinite(m) && m > 1e-9) {
      const sigma = m * 1.4826;
      lo = med - madMultLo * sigma;
      hi = med + madMultHi * sigma;
    } else {
      lo = pLo;
      hi = pHi;
    }

    if (Number.isFinite(pLo)) lo = Math.min(lo, pLo);
    if (Number.isFinite(pHi)) hi = Math.max(hi, pHi);

    if (hardCapNumeric != null && isLapLike) {
      hi = Math.min(hi, hardCapNumeric);
    }

    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) continue;

    stats[c] = {
      lo,
      hi,
      median: med,
      mad: m,
      sigma: Number.isFinite(m) && m > 1e-9 ? m * 1.4826 : NaN,
      rawN: rawVals.length,
      scaleN: vals.length,
    };
  }

  return stats;
}

/**
 * Builds one pooled heatmap scale across many lap columns.
 *
 * This is what keeps a single absurd lap from washing out the entire lap-by-lap
 * table. The absurd lap still displays and clamps to full red.
 */
function buildPooledHeatStat(rows, cols, opts = {}) {
  const {
    lowerBetterFn = () => true,
    hardCapNumeric = null,
    madMultLo = 2.5,
    madMultHi = 3.5,
    pctLo = 0.1,
    pctHi = 0.9,

    // Scale cleaning options
    cleanScale = true,
    cleanPctLo = 0.05,
    cleanPctHi = 0.85,
    cleanMadLo = 3.0,
    cleanMadHi = 2.5,
  } = opts;

  const vals = [];

  for (const c of cols) {
    if (!lowerBetterFn(c)) continue;

    for (const r of rows) {
      const v = num(r?.[c]);
      if (!Number.isFinite(v)) continue;

      if (
        hardCapNumeric != null &&
        (numericColName(c) || bestRankColName(c)) &&
        v > hardCapNumeric
      ) {
        continue;
      }

      vals.push(v);
    }
  }

  if (vals.length < 4) return null;

  const rawN = vals.length;

  if (cleanScale) {
    const cleanedVals = cleanHeatVals(vals, {
      pctLo: cleanPctLo,
      pctHi: cleanPctHi,
      madLo: cleanMadLo,
      madHi: cleanMadHi,
      minKeep: 4,
    });

    vals.length = 0;
    vals.push(...cleanedVals);
  }

  if (vals.length < 4) return null;

  const med = median(vals);
  const m = mad(vals);
  const pLo = percentile(vals, pctLo);
  const pHi = percentile(vals, pctHi);

  let lo;
  let hi;

  if (Number.isFinite(m) && m > 1e-9) {
    const sigma = m * 1.4826;
    lo = med - madMultLo * sigma;
    hi = med + madMultHi * sigma;
  } else {
    lo = pLo;
    hi = pHi;
  }

  if (Number.isFinite(pLo)) lo = Math.min(lo, pLo);
  if (Number.isFinite(pHi)) hi = Math.max(hi, pHi);

  if (hardCapNumeric != null) {
    hi = Math.min(hi, hardCapNumeric);
  }

  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return null;

  return {
    lo,
    hi,
    median: med,
    mad: m,
    sigma: Number.isFinite(m) && m > 1e-9 ? m * 1.4826 : NaN,
    rawN,
    scaleN: vals.length,
  };
}

const lerp = (a, b, t) => a + (b - a) * t;

/**
 * Applies the actual color to each cell.
 *
 * Values outside the cleaned scale do NOT stretch the scale. They simply clamp:
 * - better than scale = full green
 * - worse than scale = full red
 */
const getRobustHeatStyle = (value, stat, isDark, lowerBetter = true) => {
  if (!Number.isFinite(value) || !stat) return {};
  if (
    !Number.isFinite(stat.lo) ||
    !Number.isFinite(stat.hi) ||
    stat.hi <= stat.lo
  ) {
    return {};
  }

  let t = (value - stat.lo) / (stat.hi - stat.lo);
  t = clamp(t, 0, 1);

  if (!lowerBetter) t = 1 - t;

  let hue;
  let sat;
  let light;
  let text;
  let shadow;

  if (isDark) {
    if (t <= 0.5) {
      const p = t / 0.5;
      hue = lerp(128, 52, p);
      sat = lerp(58, 78, p);
      light = lerp(34, 30, p);
    } else {
      const p = (t - 0.5) / 0.5;
      hue = lerp(52, 6, p);
      sat = lerp(78, 80, p);
      light = lerp(30, 27, p);
    }

    text = "hsl(0 0% 92%)";
    shadow = `inset 0 0 0 1px hsl(${hue} ${Math.max(
      35,
      sat - 18
    )}% ${Math.min(42, light + 6)}% / 0.22)`;
  } else {
    if (t <= 0.5) {
      const p = t / 0.5;
      hue = lerp(128, 50, p);
      sat = lerp(52, 82, p);
      light = lerp(84, 74, p);
    } else {
      const p = (t - 0.5) / 0.5;
      hue = lerp(50, 8, p);
      sat = lerp(82, 84, p);
      light = lerp(74, 66, p);
    }

    text = "hsl(222 47% 14%)";
    shadow = `inset 0 0 0 1px hsl(${hue} ${Math.max(
      38,
      sat - 20
    )}% ${Math.max(50, light - 10)}% / 0.18)`;
  }

  return {
    backgroundColor: `hsl(${hue} ${sat}% ${light}%)`,
    color: text,
    boxShadow: shadow,
    fontWeight: t <= 0.12 || t >= 0.88 ? 700 : 600,
  };
};

// Preserve the original green/yellow/orange/red performance scale.
const getLapTableHeatStyle = (value, stat, dark) =>
  getRobustHeatStyle(value, stat, dark, true);

// Late pace sets the score. Fade can only subtract points, never reward a slow start.
const assessLateRun = (latePace, falloffVsMedian, fieldFadeScale) => {
  if (!Number.isFinite(latePace)) return { score: NaN, penalty: NaN };
  if (!Number.isFinite(falloffVsMedian)) return { score: latePace, penalty: NaN };
  const scale = Number.isFinite(fieldFadeScale) ? Math.max(0.05, fieldFadeScale) : 0.05;
  const excessFade = clamp(Math.max(0, falloffVsMedian) / scale, 0, 1);
  const lateWeakness = clamp((100 - latePace) / 100, 0, 1);
  const penalty = 15 * excessFade * lateWeakness;
  return { score: clamp(latePace - penalty, 0, 100), penalty };
};

const rollingAvg = (arr, window) => {
  const w = Math.max(1, Math.floor(window || 1));
  const out = new Array(arr.length).fill(NaN);
  let sum = 0;
  let cnt = 0;
  const q = [];
  for (let i = 0; i < arr.length; i++) {
    const x = arr[i];
    q.push(x);
    if (Number.isFinite(x)) {
      sum += x;
      cnt += 1;
    }
    if (q.length > w) {
      const y = q.shift();
      if (Number.isFinite(y)) {
        sum -= y;
        cnt -= 1;
      }
    }
    out[i] = cnt ? sum / cnt : NaN;
  }
  return out;
};

/**
 * Robust throwaway-lap filter (MAD):
 * - median + MAD
 * - keep laps within z * 1.4826 * MAD
 */
const robustSigma = (arr) => {
  const m = mad(arr);
  return Number.isFinite(m) ? m * 1.4826 : NaN;
};

const trimmedMedian = (arr, trimPct = 0.15) => {
  const xs = arr.filter(Number.isFinite).slice().sort((a, b) => a - b);
  if (!xs.length) return NaN;
  if (xs.length <= 4) return median(xs);

  const k = Math.floor(xs.length * clamp(trimPct, 0, 0.4));
  const kept = xs.slice(k, xs.length - k);
  return kept.length ? median(kept) : median(xs);
};

const localFiniteNeighbors = (arr, idx, radius = 2) => {
  const out = [];
  for (let j = Math.max(0, idx - radius); j <= Math.min(arr.length - 1, idx + radius); j++) {
    if (j === idx) continue;
    const v = arr[j];
    if (Number.isFinite(v)) out.push(v);
  }
  return out;
};

/**
 * One-sided throwaway filter: preserve fast laps and normal tire falloff.
 * Only clearly abnormal SLOW laps are removed. Masking preserves lap positions.
 */
const keepMaskHybrid = (
  arr,
  {
    globalZ = 3.0,
    localZ = 2.5,
    neighborRadius = 2,
    minSlowAbs = 2.50,
    hardSlowPct = 0.08,
  } = {}
) => {
  const xs = arr.filter(Number.isFinite);
  if (xs.length < 6) return arr.map((x) => Number.isFinite(x));

  const med = median(xs);
  const sigma = robustSigma(xs);
  const hardSlow = med + Math.max(
    minSlowAbs,
    Number.isFinite(sigma) ? globalZ * sigma : 0,
    med * hardSlowPct
  );

  return arr.map((x, i) => {
    if (!Number.isFinite(x)) return false;
    if (x > hardSlow) return false;

    // A local comparison can only reject a large SLOW spike, never a fast lap.
    const neigh = localFiniteNeighbors(arr, i, neighborRadius);
    if (neigh.length < 3) return true;
    const localMed = median(neigh);
    const localSigma = robustSigma(neigh);
    const slowJumpThresh = Math.max(
      minSlowAbs,
      Number.isFinite(localSigma) ? localZ * localSigma : 0,
      localMed * hardSlowPct
    );
    return x - localMed <= slowJumpThresh;
  });
};
const contiguousFiniteRuns = (arr, minLen = 1) => {
  const runs = [];
  let cur = [];

  for (const x of arr) {
    if (Number.isFinite(x)) {
      cur.push(x);
    } else if (cur.length) {
      if (cur.length >= minLen) runs.push(cur);
      cur = [];
    }
  }
  if (cur.length >= minLen) runs.push(cur);

  return runs;
};

const pickPrimaryRun = (runs) => {
  if (!runs.length) return [];
  return [...runs].sort((a, b) => {
    if (b.length !== a.length) return b.length - a.length;
    return median(a) - median(b);
  })[0];
};

const robustSpread = (arr) => {
  const xs = arr.filter(Number.isFinite);
  if (xs.length < 3) return NaN;
  const s = robustSigma(xs);
  if (Number.isFinite(s)) return s;
  const p25 = percentile(xs, 0.25);
  const p75 = percentile(xs, 0.75);
  return Number.isFinite(p25) && Number.isFinite(p75) ? p75 - p25 : NaN;
};

const robustTrendPer10 = (xs) => {
  const a = xs.filter(Number.isFinite);
  if (a.length < 8) return NaN;

  const seg = Math.max(4, Math.floor(a.length * 0.25));
  const first = a.slice(0, seg);
  const last = a.slice(-seg);

  const start = trimmedMedian(first, 0.15);
  const end = trimmedMedian(last, 0.15);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return NaN;

  return ((end - start) / Math.max(a.length - 1, 1)) * 10;
};

const pickLapCols = (cols) =>
  cols
    .filter((c) => numericColName(c))
    .map(Number)
    .sort((a, b) => a - b)
    .map(String);

/** Simple OLS slope of y vs x (x=0..n-1). */
const linearSlope = (ys) => {
  const pts = [];
  for (let i = 0; i < ys.length; i++) {
    const y = ys[i];
    if (Number.isFinite(y)) pts.push([i, y]);
  }
  const n = pts.length;
  if (n < 3) return NaN;

  let sumX = 0,
    sumY = 0,
    sumXX = 0,
    sumXY = 0;
  for (const [x, y] of pts) {
    sumX += x;
    sumY += y;
    sumXX += x * x;
    sumXY += x * y;
  }
  const denom = n * sumXX - sumX * sumX;
  if (Math.abs(denom) < 1e-12) return NaN;
  return (n * sumXY - sumX * sumY) / denom;
};

const robustBounds = (arr, loP = 0.1, hiP = 0.9) => {
  const xs = arr.filter(Number.isFinite);
  if (!xs.length) return [NaN, NaN];
  return [percentile(xs, loP), percentile(xs, hiP)];
};

/* NEW: softened score so nobody gets slammed to exact 0/100 */
const normLowerBetter100Soft = (x, lo, hi, softness = 0.18) => {
  if (
    !Number.isFinite(x) ||
    !Number.isFinite(lo) ||
    !Number.isFinite(hi) ||
    hi <= lo
  ) {
    return NaN;
  }

  const mid = (lo + hi) / 2;
  const scale = Math.max((hi - lo) * softness, 1e-9);
  const z = (x - mid) / scale;
  const raw = 100 / (1 + Math.exp(z));

  // keep scores from pinning to 0 / 100
  return clamp(raw, 2, 98);
};

function bestWindowMedian(xs, window) {
  const w = Math.max(1, Math.floor(window || 1));
  if (!Array.isArray(xs) || xs.length < w) return NaN;

  let best = NaN;
  for (let i = 0; i <= xs.length - w; i++) {
    const seg = xs.slice(i, i + w);
    if (seg.every(Number.isFinite)) {
      const m = median(seg);
      if (!Number.isFinite(best) || m < best) best = m;
    }
  }
  return best;
}

function getRunSegmentsByPct(xs, {
  earlyPct = 0.30,
  latePct = 0.30,
  minSeg = 5,
} = {}) {
  const a = xs.filter(Number.isFinite);
  const n = a.length;
  if (n < Math.max(minSeg * 2, 10)) {
    return { early: [], late: [], segN: 0, n };
  }

  const segN = clamp(
    Math.floor(n * Math.min(Math.max(earlyPct, 0.15), 0.45)),
    minSeg,
    Math.floor(n / 2)
  );

  const lateSegN = clamp(
    Math.floor(n * Math.min(Math.max(latePct, 0.15), 0.45)),
    minSeg,
    Math.floor(n / 2)
  );

  const finalSegN = Math.min(segN, lateSegN, Math.floor(n / 2));

  return {
    early: a.slice(0, finalSegN),
    late: a.slice(n - finalSegN),
    segN: finalSegN,
    n,
  };
}

function runLengthBoostFactor(laps, benchmark) {
  if (!Number.isFinite(laps) || !Number.isFinite(benchmark) || benchmark <= 0) {
    return 1;
  }

  const ratio = laps / benchmark;

  // slight reward for longer runs, mild penalty for shorter ones
  if (ratio >= 1) {
    return 1 + Math.min(0.10, Math.log(ratio) * 0.08);
  }

  return Math.max(0.94, 1 - (1 - ratio) * 0.05);
}

function computeGroupMedianShift(rows, metricKey, groupKey = "Group") {
  const vals = rows
    .map((r) => num(r?.[metricKey]))
    .filter(Number.isFinite);

  const overall = median(vals);
  if (!Number.isFinite(overall)) return new Map();

  const buckets = new Map();

  for (const r of rows) {
    const g = String(r?.[groupKey] ?? "").trim().toUpperCase();
    const v = num(r?.[metricKey]);
    if (!g || !Number.isFinite(v)) continue;
    if (!buckets.has(g)) buckets.set(g, []);
    buckets.get(g).push(v);
  }

  const out = new Map();
  for (const [g, arr] of buckets.entries()) {
    if (arr.length < 3) {
      out.set(g, 0);
      continue;
    }
    const gm = median(arr);
    out.set(g, gm - overall);
  }

  return out;
}

const capShift = (x) => x;

const amplifyShift = (x, mult = 1.6) => x * mult;

function Sparkline({ values, isDark }) {
  const width = 280;
  const height = 60;

  const { d, minV, maxV } = useMemo(() => {
    const xs = values.map((v) => (Number.isFinite(v) ? v : NaN));
    const finite = xs.filter(Number.isFinite);
    if (!finite.length) return { d: "", minV: NaN, maxV: NaN };
    const mn = Math.min(...finite);
    const mx = Math.max(...finite);
    const rng = mx - mn || 1;
    const n = xs.length;

    const toX = (i) => (n <= 1 ? 0 : (i / (n - 1)) * width);
    const toY = (v) => height - ((v - mn) / rng) * height;

    let path = "";
    let started = false;
    for (let i = 0; i < n; i++) {
      const v = xs[i];
      if (!Number.isFinite(v)) continue;
      const x = toX(i);
      const y = toY(v);
      if (!started) {
        path += `M ${x.toFixed(2)} ${y.toFixed(2)}`;
        started = true;
      } else {
        path += ` L ${x.toFixed(2)} ${y.toFixed(2)}`;
      }
    }
    return { d: path, minV: mn, maxV: mx };
  }, [values]);

  return (
    <div className="inline-flex flex-col items-start">
      <svg
        width={width}
        height={height}
        className={isDark ? "bg-slate-950" : "bg-white"}
      >
        <path
          d={d}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={isDark ? "text-sky-300" : "text-sky-600"}
        />
      </svg>
      <div
        className={
          isDark ? "text-[10px] text-slate-400" : "text-[10px] text-slate-500"
        }
      >
        {Number.isFinite(minV) && Number.isFinite(maxV)
          ? `range ${minV.toFixed(3)}–${maxV.toFixed(3)}`
          : "—"}
      </div>
    </div>
  );
}

/* ----------------------------- Practice analyzer v3 ----------------------------- */
// Self-contained, deterministic model. Times are seconds. No driver-name priors.
// Run position is observable; tire age, traffic, fuel and intent usually are not.
const NA3_WINDOWS = [
  [3, 5], [6, 10], [11, 15], [16, 20], [21, 25],
  [26, 30], [31, 40], [41, 50], [51, 60],
];
const na3Finite = (xs) => xs.filter(Number.isFinite);
const na3Clamp = (x, a, b) => Math.max(a, Math.min(b, x));
const na3Mean = (xs) => { const a = na3Finite(xs); return a.length ? a.reduce((s, v) => s + v, 0) / a.length : NaN; };
const na3Quantile = (xs, p) => {
  const a = na3Finite(xs).slice().sort((x, y) => x - y);
  if (!a.length) return NaN;
  const z = (a.length - 1) * na3Clamp(p, 0, 1), i = Math.floor(z);
  return a[i] + (a[Math.min(i + 1, a.length - 1)] - a[i]) * (z - i);
};
const na3Median = (xs) => na3Quantile(xs, 0.5);
const na3Mad = (xs) => { const m = na3Median(xs); return na3Median(xs.map((v) => Math.abs(v - m))); };
// Each run contributes one robust window estimate; extra laps do not buy extra votes.
const na3RunMean = (items) => {
  const a = items.filter((p) => Number.isFinite(p.v) && p.w > 0);
  const total = a.reduce((sum, p) => sum + p.w, 0);
  return total ? a.reduce((sum, p) => sum + p.w * p.v, 0) / total : NaN;
};
// An actual arithmetic average of n adjacent recorded laps. Never compact holes.
function na3Best(series, n) {
  let best = NaN;
  for (let i = 0; i + n <= series.length; i++) {
    const a = series.slice(i, i + n);
    if (a.every(Number.isFinite)) {
      const v = na3Mean(a);
      if (!Number.isFinite(best) || v < best) best = v;
    }
  }
  return best;
}
function na3Slope(points) {
  const slopes = [];
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const dx = points[j].x - points[i].x;
      if (dx >= 3) slopes.push((points[j].y - points[i].y) * 10 / dx);
    }
  }
  return slopes.length >= 3 ? na3Median(slopes) : NaN;
}
function na3Window(run, lo, hi) {
  const points = run.values.slice(lo - 1, hi).map((y, j) => ({ x: lo + j, y })).filter((p) => Number.isFinite(p.y));
  const need = Math.max(3, Math.ceil((hi - lo + 1) * 0.7));
  return points.length >= need ? { value: na3Median(points.map((p) => p.y)), n: points.length, x: na3Mean(points.map((p) => p.x)) } : null;
}
function na3BuildDriver(driver, pack, options) {
  const raw = Array.from(pack.series || [], (v) => typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : NaN);
  const nominal = na3Quantile(raw, 0.25);
  if (!Number.isFinite(nominal)) return null;
  const pitCut = nominal + Math.max(5, nominal * 0.45);
  const reasons = raw.map((v) => !Number.isFinite(v) ? 'missing' : v > pitCut ? 'pit / interruption' : v < nominal * 0.65 ? 'invalid timing' : 'kept');
  // Missing data and obvious pit/garage laps are boundaries even on lap 1.
  const chunks = []; let chunk = null;
  for (let i = 0; i < raw.length; i++) {
    if (reasons[i] !== 'kept') { chunk = null; continue; }
    if (!chunk) { chunk = { start: i, end: i, raw: [] }; chunks.push(chunk); }
    chunk.raw.push(raw[i]); chunk.end = i;
  }
  const z = na3Clamp(Number(options.filterZ) || 4, 2, 8);
  for (const c of chunks) {
    const a = c.raw;
    // One-sided ISOLATED spikes only. Never reject a whole slow tail or plateau.
    if (options.clean !== false) for (let j = 2; j < a.length - 2; j++) {
      const neighbors = [a[j - 2], a[j - 1], a[j + 1], a[j + 2]];
      const m = na3Median(neighbors);
      const threshold = Math.max(nominal * 0.025, z * 1.4826 * (na3Mad(neighbors) || 0));
      if (a[j] - m > threshold && Math.max(...neighbors) - m < threshold * 0.65) reasons[c.start + j] = 'isolated slow lap';
    }
    // Exclude a clearly abnormal entry/exit lap without changing its position.
    if (options.clean !== false && a.length >= 5) {
      for (let j = 0; j < Math.min(2, a.length); j++) {
        const next = a.slice(j + 1, j + 5);
        if (a[j] > na3Median(next) + Math.max(0.5, nominal * 0.035)) reasons[c.start + j] = 'out / warmup lap';
      }
      if (a[a.length - 1] > na3Median(a.slice(-5, -1)) + Math.max(0.7, nominal * 0.04)) reasons[c.end] = 'in / interruption lap';
    }
  }
  const masked = raw.map((v, i) => reasons[i] === 'kept' ? v : NaN);
  let prior = null, establishedOpening = NaN, hasRaceRun = false;
  const runs = chunks.map((c, i) => {
    const key = `${driver}:${c.start + 1}`;
    const override = options.runOverrides?.[key] || 'auto';
    const values = masked.slice(c.start, c.end + 1);
    const n = na3Finite(values).length;
    const opening = na3Best(values.slice(1, 10), 3);
    const priorTail = prior ? na3Median(prior.values.slice(-5)) : NaN;
    const recovery = Number.isFinite(priorTail) && Number.isFinite(opening) ? priorTail - opening : NaN;
    // A recovery is evidence, not proof of fresh tires. No stitching across stops.
    const returnsToOpening = !Number.isFinite(establishedOpening) || opening <= establishedOpening + Math.max(0.20, nominal * 0.01);
    const resetEvidence = n >= 7 && returnsToOpening && Number.isFinite(recovery)
      ? na3Clamp((recovery - nominal * 0.004) / Math.max(0.15, nominal * 0.02), 0, 1) : 0;
    const firstRaceRun = !hasRaceRun && n >= 8;
    const inferredComparable = firstRaceRun || resetEvidence >= 0.6;
    const profileEligible = override !== 'exclude';
    let context = override === 'fresh' ? 'Fresh confirmed by user' : override === 'race' ? 'Comparable race run supplied by user' : override === 'used' ? 'Used tires; estimated age continues' : override === 'exclude' ? 'Excluded by user' : firstRaceRun ? 'First substantial run; tires unknown' : resetEvidence >= 0.6 ? 'Possible tire refresh' : 'Possible continuation / uncertain intent';
    const quality = override === 'exclude' ? 0 : override === 'fresh' ? 1 : override === 'used' ? 0.5 : 0.75 + 0.1 * resetEvidence;
    const run = { key, start: c.start + 1, end: c.end + 1, values, n, opening, recovery, resetEvidence, context, quality, override, profileEligible };
    if (n >= 8) hasRaceRun = true;
    if (inferredComparable && Number.isFinite(opening)) establishedOpening = Number.isFinite(establishedOpening) ? Math.min(establishedOpening, opening) : opening;
    prior = run; return run;
  });
  // Reconstruct an estimated tire-position profile without discarding stop-separated
  // laps. Raw segments remain separate for actual consecutive averages and slopes.
  const profiles = [];
  let profile = null;
  for (const run of runs) {
    if (!run.profileEligible) {
      if (profile) profile.values.push(NaN, ...run.values.map(() => NaN));
      continue;
    }
    const firstSubstantial = run.context.startsWith('First substantial');
    const reset = !profile || run.override === 'fresh' || run.override === 'race' ||
      (run.override === 'auto' && (options.contextMode === 'all' || firstSubstantial || run.resetEvidence >= 0.6));
    if (reset) {
      profile = { values: [], segments: [], quality: run.quality, n: 0, start: run.start };
      profiles.push(profile);
    } else {
      profile.values.push(NaN); // unobserved pit/transit mileage; never compact it
      profile.quality = Math.min(profile.quality, run.quality, 0.65);
      run.context += '; linked at estimated tire position';
    }
    run.ageOffset = profile.values.length;
    profile.segments.push(run);
    profile.values.push(...run.values);
    profile.n += run.n;
  }
  const eligible = profiles.filter((r) => r.quality > 0 && r.n >= 1);
  // Short bursts are visible in actual bests, but cannot stand in for race runs.
  const bins = NA3_WINDOWS.map(([lo, hi]) => {
    const obs = eligible.map((run) => ({ run, win: na3Window(run, lo, hi) })).filter((p) => p.win);
    return { lo, hi, obs, value: na3RunMean(obs.map(({ run, win }) => ({ v: win.value, w: run.quality }))) };
  });
  const usedMasked = masked.map((v, i) => runs.some((r) => r.quality > 0 && i + 1 >= r.start && i + 1 <= r.end) ? v : NaN);
  const row = { Driver: driver, Group: String(pack.group || '').trim().toUpperCase(), nominal,
    __seriesRaw: raw, __seriesMasked: usedMasked, __runs: runs, __profiles: profiles, __bins: bins, __reasons: reasons,
    'Raw Laps': na3Finite(raw).length, 'Kept Laps': na3Finite(usedMasked).length,
    'Pit Breaks': reasons.filter((s) => s === 'pit / interruption').length,
    'Runs': runs.filter((r) => r.profileEligible).length, 'Tire Profiles': eligible.length, 'Recorded Runs': runs.length, 'Longest Run': Math.max(0, ...runs.filter((r) => r.profileEligible).map((r) => r.values.length)),
    'Possible Refreshes': runs.filter((r) => r.resetEvidence >= 0.6 && r.override === 'auto').length,
    'Tire History': runs.some((r) => r.override === 'auto' && r.n >= 5) ? 'Unverified' : 'User classified',
  };
  row['Removed %'] = row['Raw Laps'] ? 100 * (row['Raw Laps'] - row['Kept Laps']) / row['Raw Laps'] : NaN;
  for (const n of [1, 3, 5, 10, 15, 20, 25]) row[`Best ${n}`] = na3Best(usedMasked, n);
  for (const [lo, hi] of [[3, 5], [6, 15], [16, 25], [26, 40], [41, 60]]) {
    row[`Run ${lo}-${hi}`] = na3RunMean(eligible.map((run) => ({ v: na3Window(run, lo, hi)?.value, w: run.quality })));
  }
  return row;
}

function analyzePracticeV3(input, options = {}) {
  const entries = input instanceof Map ? [...input.entries()] : Object.entries(input || {});
  const base = entries.map(([name, pack]) => na3BuildDriver(name, pack, options)).filter(Boolean);
  const candidates = base.filter((r) => r.__profiles.some((run) => run.quality > 0 && run.n >= 10));
  if (!base.length) return [];
  const fieldNominal = na3Median(base.map((r) => r.nominal));
  // Auto horizon uses a shared comparison cohort, never a driver's own run length.
  const cohortMinimum = Math.min(candidates.length, Math.max(4, Math.ceil(candidates.length * 0.35)));
  const supported = (h) => candidates.filter((r) => r.__bins.filter((b) => b.hi <= h).every((b) => Number.isFinite(b.value)));
  const autoHorizon = [40, 30, 25, 20, 15, 10, 5].find((h) => supported(h).length >= Math.max(2, cohortMinimum)) || 15;
  const requestedHorizon = Number(options.horizon);
  const horizon = [10, 15, 20, 25, 30, 40, 50, 60].includes(requestedHorizon) ? requestedHorizon : autoHorizon;
  const activeIdx = NA3_WINDOWS.map((b, i) => b[1] <= horizon ? i : -1).filter((i) => i >= 0);
  let cohort = supported(horizon);
  const stableCohort = cohort.length >= Math.max(2, Math.min(4, candidates.length));
  if (!stableCohort) cohort = candidates;
  const baseline = NA3_WINDOWS.map((_, i) => {
    const values = cohort.map((r) => r.__bins[i].value).filter(Number.isFinite);
    const min = Math.max(2, Math.min(4, candidates.length));
    return values.length >= min ? { value: na3Median(values), n: values.length } : null;
  });
  const groupCount = new Set(candidates.map((r) => r.Group).filter(Boolean)).size;
  // All laps 3..horizon have equal time weight. Lap 1-2 stays diagnostic.
  const totalWeight = activeIdx.reduce((s, i) => s + NA3_WINDOWS[i][1] - NA3_WINDOWS[i][0] + 1, 0) || 1;
  const activeWeight = activeIdx.reduce((s, i) => s + (baseline[i] ? NA3_WINDOWS[i][1] - NA3_WINDOWS[i][0] + 1 : 0), 0) / totalWeight;
  for (const r of base) {
    let deltaSum = 0, observedWeight = 0, informationWeight = 0;
    const residuals = [], runDeltas = [], segmentFades = [], midFades = [], lateFades = [];
    const eligible = r.__profiles.filter((run) => run.quality > 0 && run.n >= 1);
    for (const i of activeIdx) {
      const bin = r.__bins[i], ref = baseline[i];
      if (!ref || !Number.isFinite(bin.value)) continue;
      const weight = (bin.hi - bin.lo + 1) / totalWeight;
      const localDelta = bin.value - ref.value;
      const quality = na3Mean(bin.obs.map((p) => p.run.quality));
      deltaSum += weight * localDelta;
      observedWeight += weight;
      informationWeight += weight * quality;
    }
    for (const run of eligible) {
      for (const segment of run.segments) {
        const offset = segment.ageOffset || 0;
        const points = segment.values.map((y, j) => ({ x: offset + j + 1, y })).filter((p) => p.x >= 6 && p.x <= horizon && Number.isFinite(p.y));
        const slope = na3Slope(points);
        if (points.length >= 12 && Number.isFinite(slope)) segmentFades.push({ v: slope, w: run.quality });
        for (const [lo, hi, dest] of [[6, 25, midFades], [26, horizon, lateFades]]) {
          const p = points.filter((p) => p.x >= lo && p.x <= hi);
          const v = na3Slope(p);
          if (p.length >= 10 && Number.isFinite(v)) dest.push({ v, w: run.quality });
        }
        // Never measure slope or local noise across a pit break.
        for (let j = 2; j < segment.values.length - 2 && offset + j < horizon; j++) {
          const v = segment.values[j], around = segment.values.slice(j - 2, j + 3);
          if (offset + j >= 5 && Number.isFinite(v) && na3Finite(around).length >= 4) residuals.push(Math.abs(v - na3Median(around)));
        }
      }
      const d = [], w = [];
      for (const i of activeIdx) {
        const win = na3Window(run, ...NA3_WINDOWS[i]);
        if (win && baseline[i]) { d.push(win.value - baseline[i].value); w.push(NA3_WINDOWS[i][1] - NA3_WINDOWS[i][0] + 1); }
      }
      if (w.length >= 2) runDeltas.push(d.reduce((s, v, j) => s + v * w[j], 0) / w.reduce((a, b) => a + b, 0));
    }
    r.__coverage = observedWeight;
    r.__information = informationWeight;
    r['Observed Delta'] = observedWeight ? deltaSum / observedWeight : NaN;
    // Missing windows contribute field-neutral delta. This is explicit shrinkage,
    // not an invented late pace or a penalty for merely running more laps.
    r.__gradeDelta = deltaSum;
    r['Coverage %'] = 100 * observedWeight;
    r['Field Coverage %'] = 100 * activeWeight;
    r['Comparison Horizon'] = horizon;
    r['Comparison Cohort'] = cohort.length;
    r['Baseline Mode'] = stableCohort ? 'Common-driver cohort' : 'Available drivers per window';
    r['Fade / 10'] = na3RunMean(segmentFades);
    r['Mid Fade / 10'] = na3RunMean(midFades);
    r['Late Fade / 10'] = na3RunMean(lateFades);
    r['Residual MAD'] = na3Median(residuals);
    r['Run Disagreement'] = runDeltas.length >= 2 ? na3Mad(runDeltas) : NaN;
    r.__curve = r.__bins.map((b, i) => ({ lap: (b.lo + b.hi) / 2, time: Number.isFinite(b.value) ? b.value : null, field: baseline[i]?.value ?? null }));
    const rep = eligible.slice().sort((a, b) => {
      const count = (run) => na3Finite(run.values.slice(0, horizon)).length;
      return count(b) - count(a) || a.start - b.start;
    })[0];
    r.__usableRun = rep?.values || [];
    r['Usable Run Laps'] = rep ? na3Finite(rep.values.slice(0, horizon)).length : 0;
    const withinHorizonN = eligible.reduce((s, run) => s + na3Finite(run.values.slice(2, horizon)).length, 0);
    const sample = withinHorizonN / (withinHorizonN + 8);
    const noise = Number.isFinite(r['Residual MAD']) ? 1 / (1 + r['Residual MAD'] / Math.max(0.08, fieldNominal * 0.005)) : 0.65;
    const repeat = Number.isFinite(r['Run Disagreement']) ? 1 / (1 + r['Run Disagreement'] / Math.max(0.1, fieldNominal * 0.0075)) : 0.85;
    r.Confidence = 100 * informationWeight * Math.sqrt(sample) * Math.sqrt(noise * repeat) * (stableCohort ? 1 : 0.8) * (groupCount > 1 ? 0.9 : 1);
    r['Model Notes'] = [
      r['Tire History'] === 'Unverified' ? 'Tire positions are estimated; changes are not confirmed' : 'Tire context supplied by user; profile ages remain estimates',
      observedWeight < 0.95 ? 'Missing raw windows remain missing; phase-score estimates are labeled' : '',
      !stableCohort ? 'Thin long-run reference field' : '',
      groupCount > 1 ? 'Groups are not automatically equalized; track and roster effects are confounded' : '',
      !Number.isFinite(r['Run Disagreement']) ? 'Limited repeat-run evidence' : '',
      `All non-excluded usable segments retained; ${options.contextMode === 'all' ? 'independent post-stop profiles' : 'estimated tire age carried across likely continuation stops'}`,
    ].filter(Boolean).join('; ');
  }
  const scaleValues = candidates.map((r) => r['Observed Delta']).filter(Number.isFinite);
  // Resistant to backmarkers and tiny field gaps. Scores describe THIS session.
  const scale = Math.max(fieldNominal * 0.002, 1.4826 * (na3Mad(scaleValues) || 0));
  const scoreDelta = (d) => Number.isFinite(d) ? na3Clamp(50 - 45 * Math.tanh(d / (1.5 * scale)), 5, 95) : NaN;
  const intervalScore = (r, lo, hi) => {
    const d = [], weights = [];
    for (let i = 0; i < NA3_WINDOWS.length; i++) {
      const [a, b] = NA3_WINDOWS[i];
      if (a >= lo && b <= hi && baseline[i] && Number.isFinite(r.__bins[i].value)) {
        d.push(r.__bins[i].value - baseline[i].value); weights.push(b - a + 1);
      }
    }
    return weights.length ? scoreDelta(d.reduce((s, v, j) => s + v * weights[j], 0) / weights.reduce((a, b) => a + b, 0)) : NaN;
  };
  for (const r of base) {
    r['Practice Grade'] = r.__coverage > 0 ? scoreDelta(r.__gradeDelta) : NaN;
    r['Grade Basis'] = 'Run profile';
    // A short practice program still supplies evidence. Use only matched
    // recorded positions and shrink that estimate strongly toward field neutral.
    // Do not fabricate long-run windows or override an explicitly excluded run.
    if (!Number.isFinite(r['Practice Grade'])) {
      const deltas = [];
      for (const run of r.__runs.filter((run) => run.quality > 0 && run.override !== 'used')) {
        for (let j = 0; j < Math.min(15, run.values.length); j++) {
          if (!Number.isFinite(run.values[j])) continue;
          const peers = candidates.filter((p) => p.Driver !== r.Driver).map((p) =>
            na3Median(p.__runs.filter((x) => x.profileEligible && x.quality > 0).map((x) => x.values[j]))
          ).filter(Number.isFinite);
          if (peers.length >= 2) deltas.push(run.values[j] - na3Median(peers));
        }
      }
      if (deltas.length) {
        const observed = na3Median(deltas), strength = Math.min(0.35, deltas.length / (deltas.length + 20));
        r['Practice Grade'] = scoreDelta(observed * strength);
        r['Observed Grade'] = scoreDelta(observed);
        r['Observed Delta'] = observed;
        r['Model Delta'] = observed * strength;
        r['Grade Basis'] = 'Limited-lap estimate';
        r.Confidence = Math.min(25, 25 * strength);
        r['Model Notes'] += '; Overall grade estimated from matched short-lap evidence with strong shrinkage; long-run windows remain unobserved';
      }
    }
    if (r['Grade Basis'] === 'Run profile') r['Observed Grade'] = scoreDelta(r['Observed Delta']);
    r['Short Run'] = intervalScore(r, 3, 15);
    r['Medium Run'] = intervalScore(r, 16, 25);
    r['Long Run'] = intervalScore(r, 26, 40);
    r['Extended Run'] = intervalScore(r, 41, 60);
    for (const [label, lo, hi] of [['Short Run',3,15],['Medium Run',16,25],['Long Run',26,40],['Extended Run',41,60]]) {
      r[label + ' Estimated'] = !Number.isFinite(r[label]);
      if (r[label + ' Estimated'] && Number.isFinite(r['Observed Delta'])) {
        const available = r.__bins.filter((b, i) => baseline[i] && Number.isFinite(b.value));
        const center = (lo + hi) / 2;
        const nearest = available.slice().sort((a,b) => Math.abs((a.lo+a.hi)/2-center)-Math.abs((b.lo+b.hi)/2-center))[0];
        const index = nearest ? r.__bins.indexOf(nearest) : -1;
        const delta = nearest ? nearest.value - baseline[index].value : r['Observed Delta'];
        const distance = nearest ? Math.abs((nearest.lo+nearest.hi)/2-center) : center;
        r[label] = scoreDelta(delta / (1 + distance / 15));
      }
    }
    r.Consistency = Number.isFinite(r['Residual MAD']) ? 100 / (1 + r['Residual MAD'] / Math.max(0.035, fieldNominal * 0.002)) : NaN;
    if (r['Grade Basis'] === 'Run profile') r['Model Delta'] = r.__coverage > 0 ? r.__gradeDelta : NaN;
    // Sensitivity is diagnostic, not a probability interval or a tire-age model.
    const runDeltas = r.__profiles.filter((run) => run.quality > 0).map((run) => {
      let sum = 0, weight = 0;
      for (const i of activeIdx) {
        const win = na3Window(run, ...NA3_WINDOWS[i]);
        if (win && baseline[i]) { const w = NA3_WINDOWS[i][1] - NA3_WINDOWS[i][0] + 1; sum += w * (win.value - baseline[i].value); weight += w; }
      }
      return weight >= 8 ? scoreDelta(sum / weight) : NaN;
    }).filter(Number.isFinite);
    r['Run Grade Range'] = runDeltas.length ? `${Math.min(...runDeltas).toFixed(1)}–${Math.max(...runDeltas).toFixed(1)}` : '—';
    const grade = r['Practice Grade'];
    r['Grade Status'] = !Number.isFinite(grade) ? 'Unrated' : r.__coverage < 0.5 ? 'Limited evidence' : 'Ranked';
    r.Tier = !Number.isFinite(grade) ? 'Unrated' : grade >= 80 ? 'Strong' : grade >= 65 ? 'Above field' : grade >= 40 ? 'Competitive' : grade >= 25 ? 'Below field' : 'Weak';
    r['Profile Tag'] = r.__coverage < 0.5 ? 'Limited run coverage' :
      r['Short Run'] >= 70 && Number.isFinite(r['Long Run']) && r['Long Run'] < 45 ? 'Quick early / weaker long run' :
      r['Long Run'] >= 70 && r['Short Run'] < 55 ? 'Builds relative strength' :
      r['Short Run'] >= 65 && r['Medium Run'] >= 65 && r['Long Run'] >= 65 ? 'Speed across the run' :
      r['Long Run'] >= 65 ? 'Strong long-run pace' :
      r.Confidence < 40 ? 'Context / sample uncertainty' : 'Mixed run profile';
  }
  if (!options.skipSensitivity) {
    const alternate = analyzePracticeV3(input, { ...options, horizon, contextMode: options.contextMode === 'all' ? 'screened' : 'all', skipSensitivity: true });
    const altMap = new Map(alternate.map((r) => [r.Driver, r]));
    for (const r of base) {
      const alt = altMap.get(r.Driver), altGrade = alt?.['Practice Grade'];
      r['Alternate Context Grade'] = altGrade;
      r['Context Sensitivity'] = Number.isFinite(altGrade) && Number.isFinite(r['Practice Grade']) ? Math.abs(altGrade - r['Practice Grade']) : NaN;
      if (Number.isFinite(r['Context Sensitivity'])) r.Confidence *= 1 / (1 + r['Context Sensitivity'] / 30);
      if (r['Context Sensitivity'] >= 12) r['Model Notes'] += '; Grade sensitive to uncertain post-stop run context';
    }
  }
  const ranked = base.filter((r) => Number.isFinite(r['Practice Grade'])).slice().sort((a, b) => b['Practice Grade'] - a['Practice Grade'] || a.Driver.localeCompare(b.Driver));
  ranked.forEach((r, i) => { r.Rank = i + 1; r.Quintile = `Q${Math.min(5, Math.ceil((i + 1) * 5 / ranked.length))}`; });
  return base.map((r) => ({ ...r, Rank: r.Rank ?? null, Quintile: r.Quintile || '', 'Model Version': '3.2.0' }));
}
/* ----------------------------- End practice analyzer v3 ----------------------------- */


function gradeTier(score) {
  if (!Number.isFinite(score)) return "";
  if (score >= 90) return "Elite";
  if (score >= 80) return "Strong";
  if (score >= 70) return "Solid";
  if (score >= 60) return "Neutral";
  if (score >= 50) return "Weak";
  return "Poor";
}

function fmtSigned(v, digits = 3) {
  if (!Number.isFinite(v)) return "";
  if (Math.abs(v) < 0.0005) return "0.000";
  return `${v > 0 ? "+" : ""}${v.toFixed(digits)}`;
}
/* ----------------------------- HUB PAGE ----------------------------- */
export default function NascarPracticePage({
  title,
  summarySrc,
  lapsSrc,
  showSources = false,
}) {
  const SUMMARY_SRC = summarySrc;
  const LAPS_SRC = lapsSrc;
  const SHOW_SOURCES = showSources;

  const consMetaUrl = SUMMARY_SRC.replace(/practice_cons\.json$/, "meta.json");
  const lapsMetaUrl = LAPS_SRC.replace(/practice_laps\.json$/, "meta.json");
  const consUpdated = useLastUpdated(SUMMARY_SRC, consMetaUrl);
  const lapsUpdated = useLastUpdated(LAPS_SRC, lapsMetaUrl);
  const updatedAt = useMemo(() => {
    if (consUpdated && lapsUpdated)
      return new Date(Math.max(+consUpdated, +lapsUpdated));
    return consUpdated || lapsUpdated || null;
  }, [consUpdated, lapsUpdated]);

  // ----------------------- THEME -----------------------
  const [theme, setTheme] = useState("dark");
  const isDark = theme === "dark";

  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";

  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm";

  const headerBgCell = isDark ? "bg-slate-900" : "bg-slate-100";
  const rowOddClass = isDark ? "bg-slate-950" : "bg-white";
  const rowEvenClass = isDark ? "bg-slate-900/80" : "bg-gray-50";
  const tableTextClass = isDark ? "text-slate-100" : "text-slate-900";
  const stickyCellBg = isDark ? "bg-slate-950" : "bg-white";

  // Keep all page-level sticky cells below the global site navigation.
  const Z = useMemo(
    () => ({
      bodyPinned: 5,
      head: 10,
      headPinned: 15,
    }),
    []
  );

  const [active, setActive] = useState("summary");

  const { data: consData, err: consErr, loading: consLoading } =
    useJson(SUMMARY_SRC);
  const { data: lapsData, err: lapsErr, loading: lapsLoading } =
    useJson(LAPS_SRC);

  const summaryRows = useMemo(() => {
    if (!consData) return [];
    if (Array.isArray(consData)) return consData;
    if (Array.isArray(consData?.rows)) return consData.rows;
    return [];
  }, [consData]);

  const rows = useMemo(() => {
    if (!lapsData) return [];
    if (Array.isArray(lapsData)) return lapsData;
    if (Array.isArray(lapsData?.rows)) return lapsData.rows;
    return [];
  }, [lapsData]);

  const summaryColsRaw = useMemo(
    () => (summaryRows.length ? Object.keys(summaryRows[0]) : []),
    [summaryRows]
  );

  const lapColsRaw = useMemo(
    () => reorderLapCols([...new Set(rows.flatMap((row) => Object.keys(row)))]),
    [rows]
  );

  const rawNumericLapCols = useMemo(() => pickLapCols(lapColsRaw), [lapColsRaw]);

  const driverKey = useMemo(() => findDriverKey(rows[0] || {}), [rows]);
  const groupKey = useMemo(
    () =>
      Object.keys(rows[0] || {}).find((k) => /^group$/i.test(k)) || "Group",
    [rows]
  );

  /* -------------------- PRACTICE SUMMARY -------------------- */
  const [sumVisible, setSumVisible] = useState([]);
  useEffect(() => {
    if (summaryColsRaw.length) setSumVisible(summaryColsRaw.map(() => true));
  }, [summaryColsRaw]);

  const sumToggleCol = (i) =>
    setSumVisible((v) => {
      const n = [...v];
      n[i] = !n[i];
      return n;
    });

  const summaryCols = summaryColsRaw.filter((_, i) => sumVisible[i]);

  const [sumSort, setSumSort] = useState({ col: "", dir: "asc" });

  const summarySorted = useMemo(() => {
    if (!sumSort.col) return summaryRows;
    return [...summaryRows].sort((a, b) =>
      cmp(a?.[sumSort.col], b?.[sumSort.col], sumSort.dir)
    );
  }, [summaryRows, sumSort]);

  const sumWidthCh = useMemo(() => {
    const w = {};
    if (!summaryRows.length) return w;
    const sample = summaryRows.slice(0, 200);
    for (const c of summaryColsRaw) {
      let maxLen = 0;
      const headerLen = String(c).length;
      for (const r of sample)
        maxLen = Math.max(maxLen, String(r?.[c] ?? "").length);
      if (/^driver/i.test(c)) w[c] = clamp(Math.max(maxLen, 12), 12, 22);
      else if (looksNumericCol(c))
        w[c] = clamp(Math.max(maxLen + 2, 6), 6, 14);
      else w[c] = clamp(Math.max(maxLen, Math.min(headerLen, 12)), 10, 16);
    }
    return w;
  }, [summaryRows, summaryColsRaw]);

  const summaryHeat = useMemo(() => {
    return buildRobustHeatStats(summarySorted, summaryCols, {
      lowerBetterFn: lowerIsBetter,
      hardCapNumeric: null,
      madMultLo: 2.0,
      madMultHi: 3.0,
      pctLo: 0.1,
      pctHi: 0.9,
    });
  }, [summaryCols, summarySorted]);


  // Sparse summary columns (for example, only one or two drivers with a 20-lap run)
  // do not have enough values for the robust scale. Since these cells are ranks,
  // use the full field size as a stable fallback so every numeric rank is colored.
  const summaryRankFallback = useMemo(() => {
    const hi = Math.max(2, summarySorted.length || summaryRows.length || 2);
    return { lo: 1, hi };
  }, [summarySorted.length, summaryRows.length]);

  const exportSummaryCSV = () => {
    const cols = summaryCols.length ? summaryCols : summaryColsRaw;
    const lines = [];
    lines.push(cols.map(escapeCSV).join(","));
    for (const r of summarySorted)
      lines.push(cols.map((c) => escapeCSV(r?.[c] ?? "")).join(","));
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `practice_summary_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  /* -------------------- LAP BY LAP -------------------- */
  const [q, setQ] = useState("");
  const [visibleCols, setVisibleCols] = useState([]);
  const [lapViewMode, setLapViewMode] = useState("chronological"); // "chronological" | "best"
  const [comparisonResetKey, setComparisonResetKey] = useState(0);
  const lapSelectionDetailsRef = useRef(null);

  useEffect(() => {
    const closeLapSelectionOnOutsideClick = (event) => {
      const details = lapSelectionDetailsRef.current;
      if (details?.open && !details.contains(event.target)) {
        details.open = false;
      }
    };

    document.addEventListener("pointerdown", closeLapSelectionOnOutsideClick);
    return () =>
      document.removeEventListener("pointerdown", closeLapSelectionOnOutsideClick);
  }, []);

  const lapDisplayColsRaw = useMemo(() => {
    if (!lapColsRaw.length) return [];
    if (lapViewMode === "chronological") return lapColsRaw;

    const nonNumeric = lapColsRaw.filter((c) => !numericColName(c));
    const bestCols = rawNumericLapCols.map((_, i) => `Best ${i + 1}`);
    return [...nonNumeric, ...bestCols];
  }, [lapColsRaw, rawNumericLapCols, lapViewMode]);

  const lapDisplayRows = useMemo(() => {
    if (!rows.length) return [];

    if (lapViewMode === "chronological") {
      return rows;
    }

    return rows.map((r) => {
      const next = {};
      for (const c of lapColsRaw) {
        if (!numericColName(c)) next[c] = r?.[c] ?? "";
      }

      const sortedLaps = rawNumericLapCols
        .map((c) => num(r?.[c]))
        .filter(Number.isFinite)
        .sort((a, b) => a - b);

      for (let i = 0; i < rawNumericLapCols.length; i++) {
        next[`Best ${i + 1}`] =
          Number.isFinite(sortedLaps[i]) ? sortedLaps[i] : "";
      }

      return next;
    });
  }, [rows, lapColsRaw, rawNumericLapCols, lapViewMode]);

  useEffect(() => {
    if (!lapDisplayColsRaw.length) return;
    setVisibleCols(
      lapDisplayColsRaw.map(() => true)
    );
  }, [lapDisplayColsRaw]);

  const visibleColNames = lapDisplayColsRaw.filter((_, i) => visibleCols[i]);

  const lapColumnGroups = useMemo(() => {
    const classify = (c) => {
      if (c === driverKey) return "Driver overview";
      if (numericColName(c) || bestRankColName(c)) return "Individual Laps";
      if (
        /^(1|3|5|10|15|20|25|30)\s*lap(_1)?$/i.test(String(c)) ||
        /^(std\s*dev|lap\s*avg|average|sh\.\s*run|short\s*run|lo\.\s*run|long\s*run)$/i.test(String(c))
      ) {
        return "Run Metrics";
      }
      return "Driver overview";
    };

    const groups = [];
    for (const c of visibleColNames) {
      const label = classify(c);
      const last = groups[groups.length - 1];
      if (last && last.label === label) last.span += 1;
      else groups.push({ label, span: 1 });
    }
    return groups;
  }, [visibleColNames, driverKey]);

  const filteredRows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return lapDisplayRows;
    return lapDisplayRows.filter((r) =>
      lapDisplayColsRaw.some((c) =>
        String(r?.[c] ?? "").toLowerCase().includes(needle)
      )
    );
  }, [lapDisplayRows, lapDisplayColsRaw, q]);

  const [sort, setSort] = useState({ col: "", dir: "asc" });

  const sortedRows = useMemo(() => {
    if (!sort.col) return filteredRows;
    return [...filteredRows].sort((a, b) =>
      cmp(a?.[sort.col], b?.[sort.col], sort.dir)
    );
  }, [filteredRows, sort]);

  const lapWidthPx = useMemo(() => Object.fromEntries(lapDisplayColsRaw.map((c) => [c,
    c === driverKey ? 200 : /^rank$/i.test(c) ? 64 : /^group$/i.test(c) ? 60 : /#of|number.*laps/i.test(c) ? 64 : 80
  ])), [lapDisplayColsRaw, driverKey]);
  const stickyLeftPx = useMemo(() => {
    const map = {}; let offset = 0;
    for (const c of visibleColNames) {
      map[c] = offset; offset += lapWidthPx[c] ?? 88;
      if (c === driverKey) break;
    }
    return map;
  }, [visibleColNames, lapWidthPx, driverKey]);
  const isPinnedCol = useCallback((c) => stickyLeftPx[c] !== undefined, [stickyLeftPx]);

  const minPerCol = useMemo(() => {
    const mins = {};
    for (const c of visibleColNames) {
      if (!numericColName(c) && !bestRankColName(c)) continue;
      let m = Infinity;
      for (const r of sortedRows) {
        const v = num(r?.[c]);
        if (Number.isFinite(v) && v < m) m = v;
      }
      if (m < Infinity) mins[c] = m;
    }
    return mins;
  }, [sortedRows, visibleColNames]);

const heatStats = useMemo(() => {
  return buildRobustHeatStats(sortedRows, visibleColNames, {
    lowerBetterFn: lowerIsBetter,
    hardCapNumeric: null,

    // final displayed heat range after scale cleaning
    madMultLo: 1.75,
    madMultHi: 2.25,
    pctLo: 0.10,
    pctHi: 0.90,

    // exclude absurd values from deciding the scale, but still color them red
    cleanScale: true,
    cleanPctLo: 0.05,
    cleanPctHi: 0.82,
    cleanMadLo: 3.0,
    cleanMadHi: 2.5,
  });
}, [sortedRows, visibleColNames]);

const lapSectionCols = useMemo(
  () => visibleColNames.filter((c) => numericColName(c) || bestRankColName(c)),
  [visibleColNames]
);

const pooledLapHeatStat = useMemo(() => {
  return buildPooledHeatStat(sortedRows, lapSectionCols, {
    lowerBetterFn: lowerIsBetter,
    hardCapNumeric: null,

    // final displayed heat range after scale cleaning
    madMultLo: 1.5,
    madMultHi: 2.0,
    pctLo: 0.10,
    pctHi: 0.90,

    // exclude absurd values from deciding the scale, but still color them red
    cleanScale: true,
    cleanPctLo: 0.05,
    cleanPctHi: 0.82,
    cleanMadLo: 3.0,
    cleanMadHi: 2.5,
  });
}, [sortedRows, lapSectionCols]);

  /* ---------- plot selection ---------- */
  const [selectedNames, setSelectedNames] = useState(() => new Set());

  const toggleRow = useCallback((name) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else if (next.size < 6) next.add(name);
      return next;
    });
  }, []);

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedNames.has(r?.[driverKey])),
    [rows, selectedNames, driverKey]
  );

      const setsEqual = useCallback((a, b) => {
    if (a.size !== b.size) return false;
    for (const x of a) if (!b.has(x)) return false;
    return true;
  }, []);

  const selectGroup = useCallback(
    (letter) => {
      const wantedArr = rows
        .filter(
          (r) =>
            String(r?.[groupKey] ?? "")
              .trim()
              .toUpperCase() === String(letter).toUpperCase()
        )
        .map((r) => r?.[driverKey])
        .filter(Boolean);

      const wanted = new Set(wantedArr);

      setSelectedNames((prev) => {
        if (setsEqual(prev, wanted)) return new Set();
        return wanted;
      });
    },
    [rows, driverKey, groupKey, setsEqual]
  );

  const selectAllPlots = useCallback(() => {
    const allArr = rows.map((r) => r?.[driverKey]).filter(Boolean);
    const allSet = new Set(allArr);

    setSelectedNames((prev) => {
      if (setsEqual(prev, allSet)) return new Set();
      return allSet;
    });
  }, [rows, driverKey, setsEqual]);

  const resetLapUI = () => {
    setQ("");
    setSort({ col: "", dir: "asc" });
    setSelectedNames(new Set());
    setLapViewMode("chronological");
    setComparisonResetKey((current) => current + 1);
  };

  const exportLapCSV = () => {
    const cols = visibleColNames.length ? visibleColNames : lapDisplayColsRaw;
    const lines = [];
    lines.push(cols.map(escapeCSV).join(","));
    for (const r of sortedRows)
      lines.push(cols.map((c) => escapeCSV(r?.[c] ?? "")).join(","));
    const blob = new Blob(["\ufeff" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `practice_laps_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  /* -------------------- LAP ANALYZER V2 -------------------- */
  const [anUseClean, setAnUseClean] = useState(true);
  const [anZ, setAnZ] = useState(4.0);
  const [anMinLaps, setAnMinLaps] = useState(0);
  const [anQ, setAnQ] = useState("");
  const [anSort, setAnSort] = useState({
    col: "Practice Grade",
    dir: "desc",
  });
  const [anSelected, setAnSelected] = useState(() => new Set());

  const [anHorizon, setAnHorizon] = useState("auto");
  const [anRunOverrides, setAnRunOverrides] = useState({});
  const [anContextMode, setAnContextMode] = useState("screened");
  const analyzerTableRef = useRef(null);
  useEffect(() => {
    // A run override belongs to this data snapshot, never a later slate.
    setAnRunOverrides({});
    setAnSelected(new Set());
  }, [LAPS_SRC, lapsData]);

  const toggleAnSelected = useCallback((name) => {
    setAnSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else if (next.size < 6) next.add(name);
      return next;
    });
  }, []);

  const analyzerPresets = [
    { id: "auto", label: "Auto run length", help: "Longest shared comparison horizon supported by the field" },
    { id: "15", label: "Short · 15 laps", help: "Prioritize observed restart and early-run speed" },
    { id: "30", label: "Medium · 30 laps", help: "Compare the first 30 run positions" },
    { id: "40", label: "Long · 40 laps", help: "Compare the first 40 run positions" },
    { id: "60", label: "Extended · 60 laps", help: "Include late-run evidence; missing coverage remains explicit" },
  ];
  const applyAnalyzerPreset = (id) => setAnHorizon(id);

  const lapColsSorted = useMemo(() => pickLapCols(lapColsRaw), [lapColsRaw]);

  const lapSeriesByDriver = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const name = r?.[driverKey];
      if (!name) continue;
      const series = lapColsSorted.map((c) => num(r?.[c]));
      const group = String(r?.[groupKey] ?? "")
        .trim()
        .toUpperCase();
      map.set(name, { series, group });
    }
    return map;
  }, [rows, driverKey, groupKey, lapColsSorted]);

  const analyzerAll = useMemo(() => analyzePracticeV3(lapSeriesByDriver, {
    horizon: anHorizon, filterZ: anZ, runOverrides: anRunOverrides, contextMode: anContextMode,
  }), [lapSeriesByDriver, anHorizon, anZ, anRunOverrides, anContextMode]);

  // Display filters never change the field baseline or the underlying rankings.
  const analyzerMetrics = useMemo(() => {
    const needle = anQ.trim().toLowerCase();
    return analyzerAll.filter((r) => r["Kept Laps"] >= anMinLaps &&
      (!needle || r.Driver.toLowerCase().includes(needle)))
      .sort((a, b) => cmp(a?.[anSort.col], b?.[anSort.col], anSort.dir));
  }, [analyzerAll, anMinLaps, anQ, anSort]);

  const analyzerHigherBetterCols = new Set([
    "Practice Grade", "Observed Grade", "Short Run", "Medium Run", "Long Run", "Extended Run",
    "Consistency", "Confidence", "Coverage %", "Kept Laps", "Longest Run", "Runs",
  ]);
  const analyzerHeat = useMemo(() => buildRobustHeatStats(analyzerAll, [
    "Practice Grade", "Observed Grade", "Short Run", "Medium Run", "Long Run", "Extended Run",
    "Confidence", "Coverage %", "Observed Delta", "Model Delta", "Run 6-15", "Run 16-25",
    "Run 26-40", "Run 41-60", "Fade / 10", "Mid Fade / 10", "Late Fade / 10", "Consistency",
  ], { lowerBetterFn: (c) => ![
    "Practice Grade", "Observed Grade", "Short Run", "Medium Run", "Long Run", "Extended Run",
    "Confidence", "Coverage %", "Consistency",
  ].includes(c), madMultLo: 2, madMultHi: 2.75, pctLo: 0.08, pctHi: 0.92 }), [analyzerAll]);

  const exportAnalyzerCSV = () => {
    const cols = [
      "Rank", "Quintile", "Driver", "Group", "Practice Grade", "Observed Grade", "Tier",
      "Short Run", "Medium Run", "Long Run", "Extended Run", "Confidence", "Coverage %",
      "Comparison Horizon", "Comparison Cohort", "Baseline Mode", "Field Coverage %",
      "Observed Delta", "Model Delta", "Profile Tag", "Tire History", "Runs", "Pit Breaks",
      "Possible Refreshes", "Recorded Runs", "Grade Basis", "Grade Status", "Alternate Context Grade", "Context Sensitivity", "Run Grade Range", "Short Run Estimated", "Medium Run Estimated", "Long Run Estimated", "Extended Run Estimated", "Run 3-5", "Run 6-15", "Run 16-25", "Run 26-40", "Run 41-60",
      "Best 1", "Best 3", "Best 5", "Best 10", "Best 15", "Best 20", "Best 25",
      "Fade / 10", "Mid Fade / 10", "Late Fade / 10", "Consistency", "Residual MAD", "Run Disagreement",
      "Raw Laps", "Kept Laps", "Longest Run", "Usable Run Laps", "Removed %", "Model Notes", "Model Version",
    ];

    const lines = [];
    lines.push(cols.map(escapeCSV).join(","));
    for (const r of analyzerMetrics) {
      lines.push(
        cols
          .map((c) => {
            const v = r?.[c];
            if (typeof v === "number" && Number.isFinite(v)) {
              return escapeCSV(v.toFixed(4));
            }
            return escapeCSV(typeof v === "number" && !Number.isFinite(v) ? "" : v ?? "");
          })
          .join(",")
      );
    }

    const blob = new Blob(["\ufeff" + lines.join("\r\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `lap_analyzer_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  };

  const analyzerSelectedRows = useMemo(() => analyzerAll.filter((r) => anSelected.has(r.Driver)).slice(0, 6), [analyzerAll, anSelected]);
  const analyzerCompareData = useMemo(() => {
    if (anUseClean) return NA3_WINDOWS.map(([lo, hi], i) => {
      const point = { lap: (lo + hi) / 2 };
      analyzerSelectedRows.forEach((r) => { point[r.Driver] = r.__curve[i]?.time ?? null; });
      return point;
    });
    const n = Math.max(0, ...analyzerSelectedRows.map((r) => r.__seriesRaw.length));
    return Array.from({ length: n }, (_, i) => {
      const point = { lap: i + 1 };
      analyzerSelectedRows.forEach((r) => {
        const v = r.__seriesRaw[i];
        // Pit/garage durations are breaks, not meaningful lap-time observations.
        point[r.Driver] = Number.isFinite(v) && r.__reasons[i] !== "pit / interruption" && r.__reasons[i] !== "invalid timing" ? v : null;
      });
      return point;
    });
  }, [analyzerSelectedRows, anUseClean]);
  const exportAnalyzerAudit = () => {
    const cols = ["Driver", "Recorded Lap", "Raw Seconds", "Used Seconds", "Classification", "Run Start", "Run Context", "Run Override", "Profile Included", "Model Context", "Comparison Horizon"];
    const lines = [cols.map(escapeCSV).join(",")];
    for (const r of analyzerMetrics) r.__seriesRaw.forEach((v, i) => {
      if (!Number.isFinite(v)) return;
      const run = r.__runs.find((x) => x.start <= i + 1 && x.end >= i + 1);
      lines.push([r.Driver, i + 1, v, Number.isFinite(r.__seriesMasked[i]) ? r.__seriesMasked[i] : "",
        run?.override === "exclude" ? "excluded run" : r.__reasons[i], run?.start ?? "", run?.context ?? "", run?.override ?? "", run?.profileEligible ?? false, anContextMode, r["Comparison Horizon"]].map(escapeCSV).join(","));
    });
    const href = URL.createObjectURL(new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = href; a.download = "practice_lap_audit.csv";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(href);
  };

  /* -------------------- render -------------------- */
  const padCell = "px-1 py-1";
  const textSz = "text-[11px] md:text-xs";
  const headerWrap = "whitespace-normal break-words leading-tight";
  const cellWrap = "whitespace-normal break-words";

  // Compact analyzer table sizing. These force even row heights and prevent
  // long labels like Profile Tag from wrapping into taller rows.
  const analyzerHeaderCell =
    "px-1 py-0 h-8 text-[11px] leading-tight align-middle";

  const analyzerBodyCell =
    "px-2 py-0 h-7 text-[11px] leading-none align-middle";

  const analyzerCellInner =
    "h-full min-h-0 flex items-center justify-center overflow-hidden";

  const analyzerCellText =
    "block w-full truncate whitespace-nowrap overflow-hidden text-ellipsis";

  const to3 = (v) =>
    typeof v === "number" && Number.isFinite(v) ? v.toFixed(3) : "";
  const to1 = (v) =>
    typeof v === "number" && Number.isFinite(v) ? v.toFixed(1) : "";

  const analyzerTableCols = [
    "Rank", "Driver", "Practice Grade", "Short Run", "Medium Run", "Long Run", "Extended Run",
    "Confidence", "Coverage %", "Grade Status", "Context Sensitivity", "Profile Tag", "Observed Delta", "Run 6-15", "Run 16-25", "Run 26-40", "Run 41-60", "Best 1", "Best 10", "Best 20",
    "Fade / 10", "Consistency", "Runs", "Longest Run", "Tire History", "Kept Laps", "Removed %", "Plot",
  ];
  const analyzerColWidths = Object.fromEntries(analyzerTableCols.map((c) => [c,
    c === "Driver" ? "190px" : c === "Profile Tag" ? "210px" : c === "Tire History" ? "130px" : c === "Rank" || c === "Plot" ? "54px" : "102px",
  ]));
  const analyzerTableMinWidth = Object.values(analyzerColWidths).reduce((sum, width) => sum + parseInt(width, 10), 0);
  const analyzerTooltips = {
    "Practice Grade": "Field-relative integrated pace at the selected horizon. Missing windows contribute neutral pace. No separate fade penalty or speed floor. Not a finishing-position probability.",
    "Observed Delta": "Average seconds per lap versus the common comparison field over observed windows only. Negative is faster; missing windows are excluded here.",
    "Short Run": "Observed field-relative pace at run positions 3–15. An est label indicates a shrunk estimate from nearby observed pace when this phase was not observed.",
    "Medium Run": "Observed field-relative pace at run positions 16–25. An est label indicates a shrunk estimate from nearby observed pace when this phase was not observed.",
    "Long Run": "Observed field-relative pace at run positions 26–40. An est label indicates a shrunk estimate from nearby observed pace when this phase was not observed.",
    "Extended Run": "Observed field-relative pace at run positions 41–60. An est label indicates a shrunk estimate from nearby observed pace when this phase was not observed.",
    "Confidence": "Heuristic evidence quality: coverage, sample, run disagreement, local noise and tire context. Not a calibrated probability.",
    "Coverage %": "Share of selected run length with comparable observed windows. A missing late window stays missing.",
    "Fade / 10": "Within-run robust slowing per 10 recorded laps, starting at lap 6 and ending at the selected horizon. Positive means slower. Not a direct measurement of tire wear.",
    "Consistency": "Local noise after accounting for the run's pace trend. Diagnostic only; does not increase Practice Grade.",
    "Tire History": "Tire positions are estimated across stops; fresh resets require supporting evidence. Set context in the run review below.",
    "Context Sensitivity": "Absolute grade change between linked tire-age profiles and independent post-stop profiles. Large values lower confidence; this is model sensitivity, not a probability interval.",
    "Grade Status": "Every supported overall grade receives a rank. Limited evidence is flagged through confidence, coverage and grade basis; unavailable long-run windows are not invented.",
  };

  const findFirstCol = useCallback((cols, patterns) => {
    return cols.find((c) => patterns.some((p) => p.test(String(c)))) || null;
  }, []);

  const insightCards = useMemo(() => {
    if (!summaryRows.length) return [];
    const driverCol = summaryColsRaw.find((c) => /^driver/i.test(c)) || summaryColsRaw[0];
    const avgCol = findFirstCol(summaryColsRaw, [/^average_1$/i, /^average$/i, /^lap avg/i]);
    const longCol = findFirstCol(summaryColsRaw, [/^lo\. run$/i, /^long run$/i, /^20 lap/i, /^15 lap/i]);
    const consistencyCol = findFirstCol(summaryColsRaw, [/^std\.? dev$/i, /stability/i, /consistency/i]);
    const lapsCol = findFirstCol(summaryColsRaw, [/^#of laps$/i, /^laps?$/i, /kept laps/i]);

    const bestRow = (col, higher = false) => {
      if (!col) return null;
      const valid = summaryRows.filter((r) => Number.isFinite(num(r?.[col])));
      if (!valid.length) return null;
      return [...valid].sort((a, b) =>
        higher ? num(b?.[col]) - num(a?.[col]) : num(a?.[col]) - num(b?.[col])
      )[0];
    };

    const card = (label, col, higher, accent, description) => {
      const row = bestRow(col, higher);
      if (!row) return null;
      const value = num(row?.[col]);
      return {
        label,
        driver: row?.[driverCol] || '—',
        value: Number.isFinite(value) ? (Number.isInteger(value) ? String(value) : value.toFixed(3)) : '—',
        accent,
        description,
      };
    };

    return [
      card('Fastest Overall', avgCol, false, 'sky', 'Best overall average'),
      card('Best Long Run', longCol, false, 'emerald', 'Strongest sustained pace'),
      card('Most Consistent', consistencyCol, false, 'violet', 'Lowest lap-time variation'),
      card('Most Laps', lapsCol, true, 'amber', 'Largest practice sample'),
    ].filter(Boolean);
  }, [summaryRows, summaryColsRaw, findFirstCol]);

  const formatAnalyzerValue = (col, v) => {
    if (col === "Plot") return "";
    if (typeof v === "number" && !Number.isFinite(v)) return "—";
    if (v === null || v === undefined) return "—";
    if (["Observed Delta", "Model Delta", "Fade / 10"].includes(col)) return Number.isFinite(v) ? `${v > 0 ? "+" : ""}${v.toFixed(3)}` : "—";
    if (/^(Run \d|Best \d)/.test(col)) return Number.isFinite(v) ? v.toFixed(3) : "—";
    if (["Rank", "Runs", "Kept Laps", "Longest Run"].includes(col)) return String(v);
    return typeof v === "number" ? v.toFixed(1) : v;
  };

  return (
    <div
      className={outerClass}
      style={{ position: "relative", zIndex: 0, isolation: "auto" }}
    >
      <style>{`
        /* Keep the app's global navigation and mega menu above this page.
           Avoid targeting every nested <header>, because this component has its own. */
        body > header,
        body > nav,
        #root > header,
        #root > nav,
        .site-header,
        .main-header,
        .navbar {
          position: relative;
          z-index: 1000 !important;
          overflow: visible !important;
        }

        body > header [role="menu"],
        body > nav [role="menu"],
        #root > header [role="menu"],
        #root > nav [role="menu"],
        .site-header [role="menu"],
        .main-header [role="menu"],
        .navbar [role="menu"],
        .mega-menu,
        .dropdown-menu,
        [data-radix-popper-content-wrapper] {
          z-index: 2000 !important;
          pointer-events: auto !important;
        }

        input[type="range"] { accent-color: #38bdf8; }
      `}</style>

      <div className="w-full px-4 md:px-6 py-5">
        <header
          className={[
          "relative z-0 mb-4",
          isDark ? "border-slate-800" : "border-slate-200",
        ].join(" ")}
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h1 className="text-xl font-bold tracking-tight">{title}</h1>
                {updatedAt && (
                  <span className={isDark ? "text-xs font-normal italic text-slate-100" : "text-xs font-normal italic text-slate-700"}>
                    Updated: {fmtUpdated(updatedAt)}
                  </span>
                )}
              </div>


            </div>

            <button
              type="button"
              onClick={() => setTheme(isDark ? "light" : "dark")}
              className={[
                "self-start rounded-md border px-3 py-1.5 text-xs font-semibold transition md:self-auto",
                isDark
                  ? "border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
              ].join(" ")}
            >
              {isDark ? "Light mode" : "Dark mode"}
            </button>
          </div>

          {SHOW_SOURCES && (
            <div className="mt-3 text-[11px] text-slate-500">
              Sources: {SUMMARY_SRC} · {LAPS_SRC}
            </div>
          )}
        </header>

        <div className={cardClass + " mb-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 px-4 py-3"}>
              <div className={"inline-flex flex-wrap items-center gap-5 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
                {[
                  ["cup", "Cup"],
                  ["xfinity", "O'Reilly"],
                  ["trucks", "Trucks"],
                ].map(([slug, label]) => {
                  const activeSeries = SUMMARY_SRC.match(/\/(cup|xfinity|trucks)\//)?.[1] ?? "cup";
                  const on = slug === activeSeries;
                  return (
                    <a
                      key={slug}
                      href={`/nascar/${slug === "xfinity" ? "oreilly" : slug}/practice`}
                      className={[
                        "inline-flex h-9 items-center border-b-2 pb-2 text-xs font-semibold transition",
                        on
                          ? "border-sky-400 " + (isDark ? "text-white" : "text-slate-900")
                          : isDark
                          ? "border-transparent text-slate-400 hover:text-slate-200"
                          : "border-transparent text-slate-500 hover:text-slate-900",
                      ].join(" ")}
                    >
                      {label}
                    </a>
                  );
                })}
              </div>
        <nav className={[
          "inline-flex flex-wrap gap-5 border-b",
          isDark ? "border-slate-800" : "border-slate-200",
        ].join(" ")}>
          {[
            { id: "summary", label: "Summary" },
            { id: "laps", label: "Lap-by-Lap" },
            { id: "analyzer", label: "Analyzer" },
          ].map((t) => {
            const on = active === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className={[
                  "-mb-px inline-flex h-9 items-center border-b-2 px-1 pb-2 text-xs font-semibold transition",
                  on
                    ? "border-sky-400 " + (isDark ? "text-white" : "text-slate-900")
                    : isDark
                    ? "border-transparent text-slate-500 hover:text-slate-300"
                    : "border-transparent text-slate-500 hover:text-slate-900",
                ].join(" ")}
                aria-pressed={on}
              >
                {t.label}
              </button>
            );
          })}
        </nav>
        </div>

        {/* -------- Practice Summary -------- */}
        {active === "summary" && (
          <section aria-label="Practice summary" className={cardClass + " mb-4"}>
            <div className={"flex flex-col gap-3 border-b px-4 py-3 md:flex-row md:items-start md:justify-between " + (isDark ? "border-slate-800" : "border-slate-200")}>
              <div>
                <h2 className="text-sm font-semibold leading-tight">Practice Summary</h2>
                <div className={isDark ? "mt-1 flex items-center gap-2 text-xs text-slate-500" : "mt-1 flex items-center gap-2 text-xs text-slate-500"}>
                  <span>Faster / better</span><span className="h-2.5 w-20 rounded-md bg-gradient-to-r from-emerald-500 via-amber-400 to-rose-500"/><span>Slower / worse</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <details className="relative">
                  <summary className={[
                    "inline-flex h-8 cursor-pointer list-none items-center rounded-md border px-3 text-xs font-semibold",
                    isDark ? "border-slate-700 bg-slate-900 text-slate-200" : "border-slate-200 bg-white text-slate-700",
                  ].join(" ")}>
                    Columns ({summaryCols.length}/{summaryColsRaw.length}) ▾
                  </summary>
                  <div className={[
                    "absolute left-0 z-50 mt-2 w-72 max-w-[calc(100vw-4rem)] rounded-md border p-3 shadow-lg",
                    isDark ? "border-slate-700 bg-slate-950" : "border-slate-200 bg-white",
                  ].join(" ")}>
                    <div className="mb-3 flex gap-2">
                      <button className="flex-1 rounded-lg bg-sky-500 px-2 py-1.5 text-xs font-bold text-slate-950" onClick={() => setSumVisible(summaryColsRaw.map(() => true))}>Show all</button>
                      <button className={isDark ? "flex-1 rounded-lg bg-slate-800 px-2 py-1.5 text-xs font-bold" : "flex-1 rounded-lg bg-slate-100 px-2 py-1.5 text-xs font-bold"} onClick={() => setSumVisible(summaryColsRaw.map(() => false))}>Hide all</button>
                    </div>
                    <div className="max-h-72 space-y-1 overflow-auto">
                      {summaryColsRaw.map((c, i) => (
                        <label key={c} className={isDark ? "flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm hover:bg-slate-900" : "flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-sm hover:bg-slate-50"}>
                          <input type="checkbox" checked={!!sumVisible[i]} onChange={() => sumToggleCol(i)} />
                          <span>{prettyColLabel(c)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </details>
                <button className="h-8 rounded-md bg-sky-500 px-3 text-xs font-semibold text-slate-950 hover:bg-sky-400" onClick={exportSummaryCSV}>
                  ↓ Export CSV
                </button>
              </div>
            </div>


            {consLoading && (
              <div className="text-sm text-slate-400 mb-6">Loading…</div>
            )}
            {consErr && (
              <div className="text-sm text-red-400 mb-6">
                Failed to load: {String(consErr)}
              </div>
            )}

            {!consLoading && !consErr && (
              <div
                className="overflow-auto rounded-b-2xl"
                style={{ maxHeight: "70vh" }}
              >
                <table
                  className="w-full table-fixed border-separate"
                  style={{ borderSpacing: 0 }}
                >
                  <colgroup>
                    {summaryCols.map((c) => (
                      <col
                        key={c}
                        style={{ width: `${sumWidthCh[c] ?? 10}ch` }}
                      />
                    ))}
                  </colgroup>

                  <thead>
                    <tr>
                      {summaryCols.map((c) => {
                        const activeCol = sumSort.col === c;
                        const arrow =
                          activeCol && sumSort.dir === "desc" ? "▼" : "▲";
                        const w = sumWidthCh[c] ?? 10;
                        const isDriver = /^driver/i.test(c);

                        return (
                          <th
                            key={c}
                            onClick={() =>
                              setSumSort((s) =>
                                s.col === c
                                  ? {
                                      col: c,
                                      dir: s.dir === "asc" ? "desc" : "asc",
                                    }
                                  : { col: c, dir: "asc" }
                              )
                            }
                            title={c}
                            className={[
                              "sticky top-0",
                              headerBgCell,
                              "font-semibold border-b",
                              isDark ? "border-slate-800/80" : "border-slate-200",
                              padCell,
                              textSz,
                              tableTextClass,
                              "cursor-pointer select-none text-center",
                              isDriver ? "left-0" : "",
                            ].join(" ")}
                            style={{
                              maxWidth: `${w}ch`,
                              zIndex: isDriver ? Z.headPinned : Z.head,
                            }}
                          >
                            <div
                              className={[
                                "inline-flex items-center gap-1 justify-center w-full",
                                headerWrap,
                              ].join(" ")}
                            >
                              {prettyColLabel(c)}{" "}
                              <span className="opacity-60">{arrow}</span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {summarySorted.map((r, i) => {
                      const zebra = i % 2 ? rowEvenClass : rowOddClass;
                      return (
                        <tr
                          key={i}
                          className={[
                            zebra,
                            "hover:bg-slate-800/50 transition-colors",
                          ].join(" ")}
                        >
                          {summaryCols.map((c) => {
                            const v = num(r?.[c]);
                            let heatStyle = {};
                            if (Number.isFinite(v) && lowerIsBetter(c)) {
                              heatStyle = getRobustHeatStyle(
                                v,
                                summaryHeat[c] || summaryRankFallback,
                                isDark,
                                true
                              );
                            }

                            const isDriver = /^driver/i.test(c);

                            return (
                              <td
                                key={c}
                                className={[
                                  "border-b",
                                  isDark
                                    ? "border-slate-800/70"
                                    : "border-slate-200/80",
                                  "p-0",
                                  textSz,
                                  tableTextClass,
                                  "h-8 text-center tabular-nums leading-none",
                                  isDriver
                                    ? `sticky left-0 ${stickyCellBg} border-r ${
                                        isDark
                                          ? "border-slate-800/80"
                                          : "border-slate-200"
                                      }`
                                    : "",
                                ].join(" ")}
                                style={{
                                  maxWidth: `${sumWidthCh[c] ?? 10}ch`,
                                  zIndex: isDriver ? Z.bodyPinned : "auto",
                                  ...heatStyle,
                                }}
                                title={String(r?.[c] ?? "")}
                              >
                                <div
                            className={[
                              "flex h-8 w-full items-center justify-center px-0 leading-none",
                              cellWrap,
                            ].join(" ")}
                          >
                                  {r?.[c] ?? ""}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    {!summarySorted.length && (
                      <tr>
                        <td
                          className={`${padCell} ${textSz} text-slate-400`}
                          colSpan={summaryCols.length}
                        >
                          No rows.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* -------- Lap-by-Lap -------- */}
{active === "laps" && (
  <div className="lap-modern" data-theme={isDark ? "dark" : "light"}>
    <style>{`
      .lap-modern { --lap-bg: #fff; --lap-panel: #f8fafc; --lap-edge: #e2e8f0; --lap-text: #18181b; --lap-muted: #64748b; --lap-accent: #0369a1; --lap-select: #e0f2fe; }
      .lap-modern[data-theme="dark"] { --lap-bg: #0f172a; --lap-panel: #111827; --lap-edge: #1e293b; --lap-text: #f1f5f9; --lap-muted: #94a3b8; --lap-accent: #7dd3fc; --lap-select: #102c43; }
      .lap-modern .lap-toolbar { background: var(--lap-bg); border: 1px solid var(--lap-edge); border-radius: 12px; padding: 8px 10px; display:flex; align-items:center; flex-wrap:wrap; gap:10px; }
      .lap-modern .lap-toolbar h2 { font-size: 13px; font-weight: 650; color: var(--lap-text); white-space:nowrap; }
      .lap-modern .lap-toolbar input[type="text"] { background: var(--lap-panel); border-color: var(--lap-edge); height: 30px; padding:4px 9px; font-size: 12px; border-radius: 8px; }
      .lap-modern .lap-toolbar button, .lap-modern .lap-toolbar summary { min-height: 28px; height:30px; padding:4px 9px; border-radius: 6px; font-size: 11px; }
      .lap-modern .lap-toolbar input[type="text"] { width:220px; }
      .lap-modern .lap-toolbar details { flex-shrink:0; }
      .lap-modern .lap-legend { display:flex; flex-wrap:wrap; align-items:center; gap:10px; font-size:11px; color:var(--lap-muted); padding: 0 2px 6px; }
      .lap-modern .lap-table-shell { border:1px solid var(--lap-edge); border-radius:10px; background:var(--lap-bg); overflow:hidden; }
      .lap-modern .lap-table-meta { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; padding:12px 16px; border-bottom:1px solid var(--lap-edge); color:var(--lap-muted); font-size:11px; }
      .lap-modern .lap-scroll { overflow:auto; max-height:68vh; scrollbar-width:thin; scrollbar-color: #475569 var(--lap-bg); isolation:isolate; }
      .lap-modern .lap-scroll::-webkit-scrollbar { width:9px; height:9px; }
      .lap-modern .lap-scroll::-webkit-scrollbar-track { background:var(--lap-bg); }
      .lap-modern .lap-scroll::-webkit-scrollbar-thumb { background:#475569; border:2px solid var(--lap-bg); border-radius:9px; }
      .lap-modern .lap-grid { table-layout:fixed; border-collapse:separate; border-spacing:0; font-size:12px; color:var(--lap-text); }
      .lap-modern .lap-grid th, .lap-modern .lap-grid td { box-sizing:border-box; }
      .lap-modern .lap-grid thead th { background:var(--lap-panel); border-color:var(--lap-edge); }
      .lap-modern .lap-grid thead tr:first-child th { height:20px; font-size:9px; letter-spacing:.08em; font-weight:650; color:var(--lap-muted); text-align:left; padding-left:10px; }
      .lap-modern .lap-grid thead tr:nth-child(2) th { height:28px; top:20px; font-size:11px; font-weight:600; color:var(--lap-muted); }
      .lap-modern .lap-grid th[aria-sort="ascending"], .lap-modern .lap-grid th[aria-sort="descending"] { color:var(--lap-accent); }
      .lap-modern .lap-grid tbody td { height:28px; border-bottom:1px solid var(--lap-edge); border-right:1px solid color-mix(in srgb,var(--lap-edge) 40%,transparent); font-size:12px; font-variant-numeric:tabular-nums; white-space:nowrap; }
      .lap-modern .lap-grid tbody td > div, .lap-modern .lap-grid tbody td > label { height:28px; }
      .lap-modern .lap-grid tbody tr:last-child td { border-bottom:0; }
      .lap-modern .lap-grid tbody tr:hover td { background-image:linear-gradient(#38bdf80b,#38bdf80b); }
      .lap-modern .lap-grid tbody td[data-pinned="true"] { background-color:var(--lap-bg); }
      .lap-modern .lap-grid td[data-driver="true"] { font-weight:600; border-right:1px solid var(--lap-edge); }
      .lap-modern .lap-grid td[data-driver="true"], .lap-modern .lap-grid th[data-driver="true"] { box-shadow:5px 0 10px -8px #0009; }
      .lap-modern .lap-grid tr[aria-selected="true"] td[data-driver="true"] { background:var(--lap-select) !important; box-shadow:inset 3px 0 var(--lap-accent),5px 0 10px -8px #0009; color:var(--lap-text); }
      .lap-modern .lap-grid input[type="checkbox"] { width:14px; height:14px; accent-color:var(--lap-accent); cursor:pointer; }
      .lap-modern .lap-grid td[data-best="true"] > div { font-weight:700; }
      .lap-modern .lap-grid .lap-col-divider { border-left:2px solid var(--lap-edge); }
      @media (max-width: 640px) {
        .lap-modern .lap-grid tbody td[data-pinned="true"]:not([data-driver="true"]) { position:static; }
        .lap-modern .lap-grid thead th[data-pinned="true"]:not([data-driver="true"]) { left:auto !important; z-index:10 !important; }
        .lap-modern .lap-grid [data-driver="true"] { left:0 !important; }
      }
      .lap-modern .lap-grid th:focus-visible { outline:2px solid var(--lap-accent); outline-offset:-3px; }
    `}</style>
    <div className="lap-toolbar mb-2">
      <h2>Lap-by-Lap</h2>
      <div className="flex flex-1 flex-wrap items-center gap-1.5">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search driver or value…"
          className={
            "w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 sm:w-72 " +
            (isDark
              ? "border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500"
              : "border-slate-300 bg-white text-slate-900 placeholder:text-slate-400")
          }
        />

        <div
          className={
            "inline-flex rounded-md border p-0.5 " +
            (isDark
              ? "border-slate-700 bg-slate-900"
              : "border-slate-200 bg-white")
          }
        >
          <button
            type="button"
            onClick={() => setLapViewMode("chronological")}
            className={
              "rounded px-3 py-1.5 text-xs font-bold " +
              (lapViewMode === "chronological"
                ? "bg-sky-500 text-slate-950"
                : isDark
                ? "text-slate-400"
                : "text-slate-500")
            }
          >
            Chronological
          </button>
          <button
            type="button"
            onClick={() => setLapViewMode("best")}
            className={
              "rounded px-3 py-1.5 text-xs font-bold " +
              (lapViewMode === "best"
                ? "bg-sky-500 text-slate-950"
                : isDark
                ? "text-slate-400"
                : "text-slate-500")
            }
          >
            Best Laps
          </button>
        </div>

        <details ref={lapSelectionDetailsRef} className="relative">
          <summary
            className={
              "cursor-pointer list-none rounded-md border px-3 py-2 text-sm font-semibold " +
              (isDark
                ? "border-slate-700 bg-slate-900 text-slate-200"
                : "border-slate-200 bg-white text-slate-700")
            }
          >
            Selection ({selectedNames.size}) ▾
          </summary>
          <div
            className={
              "absolute left-0 z-50 mt-2 w-52 rounded-md border p-2 shadow-xl " +
              (isDark
                ? "border-slate-700 bg-slate-950"
                : "border-slate-200 bg-white")
            }
          >
            {[
              ["Select all", selectAllPlots],
              ["Select Group A", () => selectGroup("A")],
              ["Select Group B", () => selectGroup("B")],
              ["Clear selection", () => setSelectedNames(new Set())],
            ].map(([label, fn]) => (
              <button
                key={label}
                onClick={fn}
                className={
                  "block w-full rounded px-3 py-2 text-left text-sm font-semibold " +
                  (isDark ? "hover:bg-slate-900" : "hover:bg-slate-50")
                }
              >
                {label}
              </button>
            ))}
          </div>
        </details>

        <button
          className={
            "rounded-md border px-3 py-2 text-sm font-semibold " +
            (isDark
              ? "border-slate-700 bg-slate-900 text-slate-200"
              : "border-slate-200 bg-white text-slate-700")
          }
          onClick={resetLapUI}
        >
          ↻ Reset
        </button>
        <button
          className="h-8 rounded-md bg-sky-500 px-3 text-xs font-semibold text-slate-950 hover:bg-sky-400"
          onClick={exportLapCSV}
        >
          ↓ Export
        </button>
      </div>
    </div>

    <div className="lap-legend">
      <span>Faster</span><span style={{ width: 88, height: 6, borderRadius: 8, background: "linear-gradient(90deg,#22c55e,#eab308,#f97316,#ef4444)" }} /><span>Slower</span>
      <span style={{ marginLeft: 8, color: "var(--lap-accent)" }}>●</span><span>Fastest in column</span>
      <span style={{ marginLeft: 8, color: isDark ? "#fbbf24" : "#a16207" }}>!</span><span>Abnormal lap</span><span style={{ marginLeft: "auto" }}>{sortedRows.length} drivers · {selectedNames.size} selected</span>
    </div>

    {lapsLoading && <div className="text-sm text-slate-400">Loading…</div>}
    {lapsErr && (
      <div className="text-sm text-red-400">
        Failed to load: {String(lapsErr)}
      </div>
    )}

    {!lapsLoading && !lapsErr && (
      <div className="lap-table-shell">
        <div className="lap-scroll" tabIndex={0} role="region" aria-label="Lap-by-lap driver data">

        <table
          className="lap-grid"
          style={{ width: visibleColNames.reduce((sum, c) => sum + (lapWidthPx[c] ?? 88), 0), minWidth: "100%", borderSpacing: 0 }}
        >
          <colgroup>
            {visibleColNames.map((c) => (
              <col key={c} style={{ width: `${lapWidthPx[c] ?? 88}px` }} />
            ))}
          </colgroup>

          <thead>
            <tr>
              {lapColumnGroups.map((group, idx) => (
                <th
                  key={`${group.label}-${idx}`}
                  colSpan={group.span}
                  className={[
                    "sticky top-0 h-5 border-b px-2 py-0 text-[10px] font-bold uppercase tracking-[0.08em]",
                    headerBgCell,
                    isDark
                      ? "border-slate-800/80 text-slate-500"
                      : "border-slate-200 text-slate-500",
                    group.label === "Individual Laps"
                      ? isDark
                        ? "border-l-2 border-l-sky-800/80"
                        : "border-l-2 border-l-sky-300"
                      : "",
                  ].join(" ")}
                  style={{ zIndex: Z.head }}
                >
                  {group.label}
                </th>
              ))}
            </tr>

            <tr>
              {visibleColNames.map((c) => {
                const activeCol = sort.col === c;
                const arrow = activeCol ? (sort.dir === "desc" ? "↓" : "↑") : "↕";
                const w = lapWidthPx[c] ?? 88;
                const pinned = isPinnedCol(c);
                const leftCh = pinned ? stickyLeftPx[c] : null;
                const isFirstLap = lapSectionCols[0] === c;

                return (
                  <th
                    key={c}
                    onClick={() =>
                      setSort((s) =>
                        s.col === c
                          ? { col: c, dir: s.dir === "asc" ? "desc" : "asc" }
                          : { col: c, dir: "asc" }
                      )
                    }
                    title={`Sort by ${prettyColLabel(c)}`}
                    tabIndex={0}
                    aria-sort={activeCol ? (sort.dir === "desc" ? "descending" : "ascending") : "none"}
                    data-driver={c === driverKey}
                    data-pinned={pinned}
                    onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSort((prev) => prev.col === c ? { col: c, dir: prev.dir === "asc" ? "desc" : "asc" } : { col: c, dir: "asc" }); } }}
                    className={[
                      "sticky top-[20px] font-semibold border-b",
                      headerBgCell,
                      isDark
                        ? "border-slate-800/80"
                        : "border-slate-200",
                      "h-7 px-1 py-0",
                      textSz,
                      tableTextClass,
                      "cursor-pointer select-none text-center",
                      pinned
                        ? `border-r ${
                            isDark ? "border-slate-800/80" : "border-slate-200"
                          }`
                        : "",
                      isFirstLap
                        ? isDark
                          ? "border-l-2 border-l-sky-800/80"
                          : "border-l-2 border-l-sky-300"
                        : "",
                    ].join(" ")}
                    style={{
                      maxWidth: `${w}px`,
                      ...(pinned ? { left: `${leftCh}px` } : {}),
                      zIndex: pinned ? Z.headPinned : Z.head,
                    }}
                  >
                    <div
                      className={[
                        "inline-flex w-full items-center justify-center gap-1",
                        headerWrap,
                      ].join(" ")}
                    >
                      {prettyColLabel(c)}
                      <span aria-hidden="true" style={{ opacity: activeCol ? 1 : 0.3, fontSize: 12 }}>{arrow}</span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {sortedRows.map((r, i) => {
              const zebra = i % 2 ? rowEvenClass : rowOddClass;
              return (
                <tr
                  key={`${r?.[driverKey] ?? "row"}-${i}`}
                  aria-selected={selectedNames.has(r?.[driverKey])}
                  className={[
                    zebra,
                    isDark
                      ? "hover:outline hover:outline-1 hover:outline-sky-700/70"
                      : "hover:outline hover:outline-1 hover:outline-sky-300",
                    "transition-colors",
                  ].join(" ")}
                >
                  {visibleColNames.map((c) => {
                    const valNum = num(r?.[c]);
                    const isLap = numericColName(c) || bestRankColName(c);
                    const isBest = isLap && isClose(valNum, minPerCol[c]);
                    const isFirstLap = lapSectionCols[0] === c;
                    const isDriver = c === driverKey;

                    const anomalyCutoff =
                      pooledLapHeatStat && Number.isFinite(pooledLapHeatStat.hi)
                        ? pooledLapHeatStat.hi +
                          Math.max(
                            2,
                            Number.isFinite(pooledLapHeatStat.sigma)
                              ? pooledLapHeatStat.sigma * 6
                              : 0
                          )
                        : Infinity;
                    const isAbnormalLap =
                      isLap && Number.isFinite(valNum) && valNum > anomalyCutoff;

                    let heatStyle = {};
                    if (!isAbnormalLap && lowerIsBetter(c) && Number.isFinite(valNum)) {
                      if (isLap && pooledLapHeatStat) {
                        heatStyle = getLapTableHeatStyle(
                          valNum,
                          pooledLapHeatStat,
                          isDark
                        );
                      } else if (heatStats[c]) {
                        heatStyle = getLapTableHeatStyle(
                          valNum,
                          heatStats[c],
                          isDark
                        );
                      }
                    }

                    if (isAbnormalLap) {
                      heatStyle = isDark
                        ? {
                            backgroundColor: "#351b2d",
                            color: "#fda4af",
                            boxShadow: "none",
                          }
                        : {
                            backgroundColor: "#fff1f2",
                            color: "#991b1b",
                            boxShadow: "inset 0 0 0 1px rgba(220,38,38,0.14)",
                          };
                    }

                    const pinned = isPinnedCol(c);
                    const leftCh = pinned ? stickyLeftPx[c] : null;

                    return (
                      <td
                        key={c}
                        data-driver={isDriver}
                        data-pinned={pinned}
                        data-best={isBest && !isAbnormalLap}
                        className={[
                          "border-b",
                          isDark
                            ? "border-slate-800/70"
                            : "border-slate-200/80",
                          "h-7 p-0 leading-none",
                          textSz,
                          tableTextClass,
                          "text-center tabular-nums",
                          pinned
                            ? `sticky ${stickyCellBg} border-r ${
                                isDark
                                  ? "border-slate-800/80"
                                  : "border-slate-200"
                              }`
                            : "",
                          isFirstLap
                            ? isDark
                              ? "border-l-2 border-l-sky-800/80"
                              : "border-l-2 border-l-sky-300"
                            : "",
                        ].join(" ")}
                        style={{
                          maxWidth: `${lapWidthPx[c] ?? 88}px`,
                          ...(pinned ? { left: `${leftCh}px` } : {}),
                          zIndex: pinned ? Z.bodyPinned : "auto",
                          backgroundColor: isDark ? (i % 2 ? "#111c30" : "#0f172a") : (i % 2 ? "#f8fafc" : "#ffffff"),
                          ...heatStyle,
                        }}
                        title={
                          isAbnormalLap
                            ? `${String(r?.[c] ?? "")} — abnormal lap / likely pit, caution, or timing issue`
                            : String(r?.[c] ?? "")
                        }
                      >
                        {isDriver ? (
                          <label className="flex h-7 min-w-0 cursor-pointer items-center gap-2 px-2 text-left leading-none">
                            <input
                              type="checkbox"
                              checked={selectedNames.has(r?.[driverKey])}
                              onChange={() => toggleRow(r?.[driverKey])}
                              onClick={(e) => e.stopPropagation()}
                              className="shrink-0"
                              aria-label={`Compare ${r?.[driverKey]}`}
                            />
                            <span className="truncate" title={String(r?.[c] ?? "")}>{r?.[c] ?? ""}</span>
                          </label>
                        ) : (
                          <div
                            className={[
                              "flex h-7 w-full items-center justify-center px-1 leading-none",
                              cellWrap,
                            ].join(" ")}
                          >
                            {Number.isFinite(valNum) && (isLap || /^(average|std\s*dev|\d+\s*lap)/i.test(c)) ? valNum.toFixed(3) : (r?.[c] ?? "—")}
                            {isAbnormalLap ? (
                              <span
                                className="ml-1 align-super text-[10px] font-bold"
                                title="Abnormal lap"
                              >
                                !
                              </span>
                            ) : isBest ? (
                              <span
                                className={
                                  isDark
                                    ? "ml-0.5 align-super text-[10px] font-bold text-sky-300"
                                    : "ml-0.5 align-super text-[10px] font-bold text-sky-700"
                                }
                                title="Fastest lap in this column"
                              >
                                ●
                              </span>
                            ) : null}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            {!sortedRows.length && (
              <tr>
                <td
                  className={`${padCell} ${textSz} text-slate-400`}
                  colSpan={visibleColNames.length}
                >
                  No rows.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    )}

    <div className="mt-8">
      <PracticeOverlayChart
        rows={selectedRows}
        allRows={rows}
        driverKey={driverKey}
        isDark={isDark}
        resetKey={comparisonResetKey}
        onSelectionChange={(names) => setSelectedNames(new Set(names))}
      />
    </div>
  </div>
)}


        {active === "analyzer" && (
          <div className="analyzer-modern" data-theme={isDark ? "dark" : "light"}>
            <style>{`
              .analyzer-modern { --an-bg:#fff; --an-panel:#f8fafc; --an-border:#e2e8f0; --an-text:#0f172a; --an-muted:#64748b; --an-selected:#e0f2fe; }
              .analyzer-modern[data-theme="dark"] { --an-bg:#020617; --an-panel:#0f172a; --an-border:#1e293b; --an-text:#f1f5f9; --an-muted:#94a3b8; --an-selected:#102c43; }
              .analyzer-modern .an-toolbar { background:var(--an-panel); border:1px solid var(--an-border); padding:8px 10px; border-radius:12px; }
              .analyzer-modern .an-toolbar input { height:30px; padding:4px 9px; font-size:12px; }
              .analyzer-modern button { min-height:28px; padding:4px 9px; font-size:11px; }
              .analyzer-modern .an-settings { padding:0; overflow:hidden; }
              .analyzer-modern .an-settings summary { cursor:pointer; padding:8px 10px; font-size:12px; font-weight:600; color:var(--an-text); }
              .analyzer-modern .an-settings > div { padding:10px; gap:12px; }
              .analyzer-modern .an-table-wrap { scrollbar-width:thin; scrollbar-color:#475569 var(--an-panel); border-radius:10px; }
              .analyzer-modern .an-grid { width:100%; color:var(--an-text); }
              .analyzer-modern .an-grid th, .analyzer-modern .an-grid td { box-sizing:border-box; }
              .analyzer-modern .an-grid th { height:32px; font-size:10px; font-weight:600; color:var(--an-muted); background:var(--an-panel); }
              .analyzer-modern .an-grid td { height:28px; font-size:11px; }
              .analyzer-modern .an-grid [data-sticky="true"] { background:var(--an-bg); }
              .analyzer-modern .an-grid th[data-sticky="true"] { background:var(--an-panel); }
              .analyzer-modern .an-grid tr[aria-selected="true"] td[data-driver="true"] { background:var(--an-selected); box-shadow:inset 3px 0 #7dd3fc; }
              .analyzer-modern .an-grid th:focus-visible { outline:2px solid #38bdf8; outline-offset:-2px; }
              @media(max-width:640px) { .analyzer-modern .an-grid td[data-sticky="true"]:not([data-driver="true"]) { position:static; } .analyzer-modern .an-grid th[data-sticky="true"]:not([data-driver="true"]) { left:auto !important; z-index:10 !important; } .analyzer-modern .an-grid [data-driver="true"] { left:0 !important; } }
            `}</style>
            <div className="an-toolbar flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
              <h2 className="text-sm font-semibold">Lap Analyzer</h2>

              <div className="flex flex-wrap items-center gap-2 ml-auto">
                <input
                  type="text"
                  value={anQ}
                  onChange={(e) => setAnQ(e.target.value)}
                  placeholder="Search driver…"
                  className={
                    "border rounded-md px-3 py-2 w-56 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                    (isDark
                      ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500"
                      : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400")
                  }
                />

                <button
                  className="px-3 py-2 text-sm rounded-md bg-slate-900/80 hover:bg-slate-800 text-slate-100 border border-slate-700/80"
                  onClick={() => setAnUseClean((v) => !v)}
                  title="Toggle shared run-profile chart versus raw recorded session laps; grades are unchanged"
                >
                  {anUseClean ? "Chart: run profile" : "Chart: session laps"}
                </button>

                <button
                  className="px-3 py-2 text-sm rounded-md bg-slate-950 text-slate-100 border border-slate-700/80"
                  onClick={() => {
                    setAnQ("");
                    setAnZ(4.0);
                    setAnMinLaps(10);
                    setAnUseClean(true);

                    setAnHorizon("auto");
                    setAnRunOverrides({});
                    setAnContextMode("screened");
                    analyzerTableRef.current?.scrollTo({ left: 0, top: 0 });

                    setAnSort({ col: "Practice Grade", dir: "desc" });
                    setAnSelected(new Set());
                  }}
                >
                  Reset
                </button>

                <button
                  className="px-3 py-2 text-sm rounded-md bg-sky-500 text-slate-950 hover:bg-sky-400 font-semibold"
                  onClick={exportAnalyzerCSV}
                >
                  Export CSV
                </button>
                <button className="px-3 py-2 text-sm rounded-md border border-slate-500" onClick={exportAnalyzerAudit}>Lap audit CSV</button>
              </div>
            </div>

            <div className={`${cardClass} p-3 mb-4`}>
              <div className="flex flex-wrap items-center gap-2">
                <div
                  className={
                    isDark ? "text-xs text-slate-300" : "text-xs text-slate-600"
                  }
                >
                  Presets:
                </div>
                {analyzerPresets.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => applyAnalyzerPreset(p.id)}
                    className={[
                      "px-3 py-1.5 rounded-md text-xs border transition",
                      "bg-slate-900/60 hover:bg-slate-800",
                      isDark
                        ? "border-slate-700 text-slate-100"
                        : "border-slate-300 text-slate-800 bg-white",
                    ].join(" ")}
                    aria-pressed={anHorizon === p.id}
                    title={p.help}
                  >
                    {p.label}
                  </button>
                ))}
                <div
                  className={
                    isDark
                      ? "text-[11px] text-slate-500 ml-2"
                      : "text-[11px] text-slate-500 ml-2"
                  }
                >
                  Comparing <b>{analyzerAll[0]?.["Comparison Horizon"] ?? "—"} profile laps</b>. {analyzerAll[0]?.["Comparison Cohort"] ?? 0} drivers in the reference cohort. Profile positions are estimated tire ages.
                </div>
              </div>
            </div>

            <details className={cardClass + " an-settings mb-3"}>
              <summary>Analyzer settings and methodology</summary>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="text-xs">Isolated slow-lap threshold: {anZ.toFixed(1)}
                  <input className="w-full" type="range" min="2" max="8" step="0.5" value={anZ} onChange={(e) => setAnZ(Number(e.target.value))} />
                  <span className="block opacity-70">Lower is stricter. Sustained slow stretches remain in the data. Pit breaks are always preserved.</span>
                </label>
                <label className="text-xs">Minimum visible clean laps: {anMinLaps}
                  <input className="w-full" type="range" min="0" max="60" step="1" value={anMinLaps} onChange={(e) => setAnMinLaps(Number(e.target.value))} />
                  <span className="block opacity-70">Display filter only; does not change field baselines or grades.</span>
                </label>
                <label className="text-xs">Post-stop comparison context
                  <select className={"block w-full border rounded p-2 my-2 " + (isDark ? "bg-slate-900 text-slate-100" : "bg-white text-slate-900")} value={anContextMode} onChange={(e) => setAnContextMode(e.target.value)}>
                    <option value="screened">Link likely tire continuations</option><option value="all">Treat post-stop runs independently</option>
                  </select>
                  <span className="block opacity-70">Retains all usable runs. Likely continuations carry estimated tire age forward; supported refreshes restart it. Context Sensitivity compares this with independent post-stop runs.</span>
                </label>
                <p className="md:col-span-3 text-xs leading-relaxed opacity-80">The grade integrates seconds gained or lost versus the reference field from estimated tire lap 3 to the selected horizon. Early, medium, long and extended pace remain visible separately. Missing windows contribute neutral pace to the overall grade and lower coverage. Unobserved phase scores use a clearly labeled estimate from nearby observed pace. Falloff and consistency explain the run; they do not add a second penalty or reward. Confidence measures evidence quality separately. Every Best N is an actual arithmetic average of N adjacent retained laps, always within a recorded segment. No best window crosses a pit or missing lap.</p>
              </div>
            </details>
            <div className="mb-2 text-[11px] leading-relaxed" style={{ color: "var(--an-muted)" }}>
              Lower lap times and negative deltas are faster. Short = 3–15, Medium = 16–25, Long = 26–40, Extended = 41–60 estimated tire positions. est marks a score estimated from nearby observed pace; actual lap-time windows remain blank when unobserved. All supported overall grades receive ranks; limited evidence is flagged through confidence and coverage. Context sensitivity shows how much uncertain runs matter. These grades describe practice, not predicted race finishes.
            </div>
            <div
              ref={analyzerTableRef}
              className={`${cardClass} an-table-wrap overflow-auto`}
              style={{ maxHeight: "60vh" }}
            >
              <table
                className="an-grid table-fixed border-separate text-[11px]"
                style={{ borderSpacing: 0, minWidth: analyzerTableMinWidth }}
              >
                <colgroup>
                  {analyzerTableCols.map((c) => (
                    <col
                      key={c}
                      style={{
                        width: analyzerColWidths[c] || "10ch",
                        minWidth: analyzerColWidths[c] || "10ch",
                      }}
                    />
                  ))}
                </colgroup>

                <thead>
                  <tr>
                    {analyzerTableCols.map((c) => {
                      const isRank = c === "Rank";
                      const isQuintile = c === "Quintile";
                      const isPinned = c === "Driver";
                      const activeCol = anSort.col === c;
                      const arrow = activeCol ? (anSort.dir === "desc" ? "↓" : "↑") : "↕";

                      return (
                        <th
                          key={c}
                          data-sticky={isPinned || isRank || isQuintile}
                          data-driver={isPinned}
                          tabIndex={c === "Plot" ? undefined : 0}
                          aria-sort={c === "Plot" ? undefined : activeCol ? (anSort.dir === "asc" ? "ascending" : "descending") : "none"}
                          title={analyzerTooltips[c] || (/^Run \d/.test(c) ? "Run-weighted median-window seconds at these estimated tire positions." : /^Best \d/.test(c) ? "Fastest actual arithmetic average of this many adjacent retained laps. Pit and missing laps break the window." : c)}
                          onKeyDown={(event) => { if (c !== "Plot" && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); setAnSort((prev) => ({ col: c, dir: prev.col === c && prev.dir === "asc" ? "desc" : "asc" })); } }}
                          onClick={() => {
                            if (c === "Plot") return;
                            setAnSort((s) =>
                              s.col === c
                                ? { col: c, dir: s.dir === "asc" ? "desc" : "asc" }
                                : { col: c, dir: analyzerHigherBetterCols.has(c) ? "desc" : "asc" }
                            );
                          }}
                          className={[
                            "sticky top-0",
                            headerBgCell,
                            "font-semibold border-b",
                            isDark ? "border-slate-800/80" : "border-slate-200",
                            analyzerHeaderCell,
                            tableTextClass,
                            c === "Plot" ? "" : "cursor-pointer select-none",
                            "text-center",
                            isPinned
                              ? `left-[23ch] ${stickyCellBg} border-r ${
                                  isDark ? "border-slate-800/80" : "border-slate-200"
                                }`
                              : isQuintile
                              ? `left-[10ch] ${stickyCellBg} border-r ${
                                  isDark ? "border-slate-800/80" : "border-slate-200"
                                }`
                              : isRank
                              ? `left-0 ${stickyCellBg} border-r ${
                                  isDark ? "border-slate-800/80" : "border-slate-200"
                                }`
                              : "",
                          ].join(" ")}
                          style={{
                            ...(isRank ? { left: 0 } : isQuintile ? { left: 56 } : isPinned ? { left: 54 } : {}),
                            zIndex: c === "Driver" || c === "Rank" || c === "Quintile" ? Z.headPinned : Z.head,
                          }}
                        >
                          <div className="relative flex h-full w-full items-center justify-center overflow-visible text-center whitespace-normal leading-tight px-1">
                            <span className="block w-full leading-tight whitespace-normal break-words">{c === "Fresh Pace" ? "Tire 1–5" : c}</span>
                            {c !== "Plot" && (
                              <span className="absolute right-0 top-1/2 -translate-y-1/2 opacity-60 text-[10px]">
                                {arrow}
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {analyzerMetrics.map((r, i) => {
                    const zebra = i % 2 ? rowEvenClass : rowOddClass;
                    const isOn = anSelected.has(r.Driver);

                    const styleFor = (col) => {
                      const v = num(r?.[col]);
                      if (col === "Fade Penalty") {
                        // Neutral zero, warm colors for deductions: never green for "slow but improving".
                        if (!Number.isFinite(v) || v <= 0) return {};
                        return getRobustHeatStyle(v, { lo: -15, hi: 15 }, isDark, true);
                      }
                      if (!Number.isFinite(v) || !analyzerHeat[col]) return {};
                      return getRobustHeatStyle(
                        v,
                        analyzerHeat[col],
                        isDark,
                        !analyzerHigherBetterCols.has(col)
                      );
                    };

                    return (
                      <tr
                        key={r.Driver}
                        aria-selected={isOn}
                        className={[zebra, "hover:bg-slate-800/50 transition-colors"].join(" ")}
                      >
                        {analyzerTableCols.map((c) => {
                          const isRank = c === "Rank";
                          const isQuintile = c === "Quintile";
                          const isPinned = c === "Driver";
                          const isPlot = c === "Plot";
                          const value = r?.[c];

                          return (
                            <td
                              key={`${r.Driver}-${c}`}
                              data-sticky={isPinned || isRank || isQuintile}
                              data-driver={isPinned}
                              title={c === "Driver" || c === "Confidence" || c === "Tire History" ? r["Model Notes"] : analyzerTooltips[c]}
                              className={[
                                "border-b",
                                isDark ? "border-slate-800/70" : "border-slate-200/80",
                                `${analyzerBodyCell} text-center tabular-nums`,
                                c === "Driver" ? tableTextClass : "",
                                c === "Practice Grade" || c === "Rank" ? "font-semibold" : "",
                                isPinned
                                  ? `sticky left-[23ch] ${stickyCellBg} border-r ${
                                      isDark ? "border-slate-800/80" : "border-slate-200"
                                    }`
                                  : isQuintile
                                  ? `sticky left-[10ch] ${stickyCellBg} border-r ${
                                      isDark ? "border-slate-800/80" : "border-slate-200"
                                    }`
                                  : isRank
                                  ? `sticky left-0 ${stickyCellBg} border-r ${
                                      isDark ? "border-slate-800/80" : "border-slate-200"
                                    }`
                                  : "",
                              ].join(" ")}
                              style={{
                                ...(isRank ? { left: 0 } : isQuintile ? { left: 56 } : isPinned ? { left: 54 } : {}),
                                zIndex: isPinned || isRank || isQuintile ? Z.bodyPinned : "auto",
                                ...(isPlot || c === "Driver" || c === "Quintile" || c === "Tier" || c === "Profile Tag" ? {} : styleFor(c)),
                              }}
                            >
                              {isPlot ? (
                                <div className={analyzerCellInner}>
                                  <input
                                    type="checkbox"
                                    checked={isOn}
                                    disabled={!isOn && anSelected.size >= 6}
                                    aria-label={`Compare analyzer ${r.Driver}`}
                                    onChange={() => toggleAnSelected(r.Driver)}
                                  />
                                </div>
                              ) : c === "Driver" ? (
                                <div className="h-full min-h-0 flex items-center justify-between gap-2 overflow-hidden text-left">
                                  <div className="font-semibold truncate">{r.Driver}</div>
                                  <div
                                    className="text-[10px] text-slate-500 shrink-0"
                                    title={
                                      anUseClean
                                        ? `${r["Kept Laps"]}/${r["Raw Laps"]} kept`
                                        : `${r["Raw Laps"]} raw laps`
                                    }
                                  >
                                    {anUseClean ? `${r["Kept Laps"]}/${r["Raw Laps"]}` : `${r["Raw Laps"]}`}
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className={analyzerCellInner}
                                  title={String(formatAnalyzerValue(c, value) ?? "")}
                                >
                                  <span className={analyzerCellText}>
                                    {formatAnalyzerValue(c, value)}{r[c + " Estimated"] && <small className="ml-1 opacity-60">est</small>}
                                  </span>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}

                  {!analyzerMetrics.length && (
                    <tr>
                      <td className={"px-2 py-2 text-xs text-slate-400"} colSpan={analyzerTableCols.length}>
                        No drivers match filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className={`${cardClass} p-4 mt-4`}>
              <div className="flex items-center justify-between gap-3 mb-3">
                <div><div className="text-sm font-bold">Driver comparison · shared axes</div>
                  <p className="text-xs opacity-70">{anUseClean ? "Robust pace at estimated tire positions; likely same-tire runs are linked across stops. Tire changes remain uncertain." : "Raw recorded session laps with pit and garage durations shown as gaps. Session lap numbers are not synchronized clock times."}</p>
                </div>
                <button className="border rounded px-3 py-1 text-xs" onClick={() => setAnSelected(new Set())}>Clear</button>
              </div>
              {!analyzerSelectedRows.length ? <p className="text-sm opacity-70">Select up to six drivers in the table to compare and review runs.</p> : <>
                <div className="flex flex-wrap gap-3 mb-2 text-xs">{analyzerSelectedRows.map((r, i) => <span key={r.Driver} style={{color: ["#38bdf8", "#fb923c", "#a78bfa", "#34d399", "#f472b6", "#facc15"][i]}}>{r.Driver}</span>)}</div>
                <div style={{ width: "100%", height: 320 }}><ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyzerCompareData} margin={{top: 8, right: 20, left: 5, bottom: 24}}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#cbd5e1"} />
                    <XAxis dataKey="lap" type="number" domain={["dataMin", "dataMax"]} label={{value: anUseClean ? "Estimated tire position" : "Recorded session lap", position: "bottom", offset: 6}} tick={{fontSize: 11}} />
                    <YAxis domain={[(v) => Math.floor(v * 10) / 10 - 0.05, (v) => Math.ceil(v * 10) / 10 + 0.05]} tickFormatter={(v) => Number(v).toFixed(2)} tick={{fontSize: 11}} width={58} />
                    <Tooltip formatter={(v, name) => [Number(v).toFixed(3) + " s", name]} labelFormatter={(v) => `Lap ${v}`} contentStyle={{background: isDark ? "#0f172a" : "#fff", border: "1px solid #64748b", fontSize: 12}} />
                    {analyzerSelectedRows.map((r, i) => <Line key={r.Driver} type="linear" dataKey={r.Driver} name={r.Driver} stroke={["#38bdf8", "#fb923c", "#a78bfa", "#34d399", "#f472b6", "#facc15"][i]} strokeWidth={2} dot={anUseClean ? {r: 3} : false} connectNulls={false} isAnimationActive={false} />)}
                  </LineChart>
                </ResponsiveContainer></div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-4">{analyzerSelectedRows.map((r) => <details key={r.Driver} className="border border-slate-500/40 rounded-lg p-3" open>
                  <summary className="cursor-pointer text-sm font-semibold">{r.Driver} · run evidence</summary>
                  <p className="text-[11px] opacity-70 my-2">{r["Model Notes"]}. Per-run observed grade range: {r["Run Grade Range"]}; this is sensitivity, not a statistical interval.</p>
                  {r.__runs.map((run) => <div key={run.key} className="border-t border-slate-500/20 py-2 flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div><b>Recorded laps {run.start}–{run.end}</b> · {run.n} retained<br/><span className="opacity-70">{run.context}{Number.isFinite(run.recovery) ? ` · recovery ${run.recovery.toFixed(3)} s` : ""}</span></div>
                    <select aria-label={`${r.Driver} run starting lap ${run.start} tire context`} value={anRunOverrides[run.key] || "auto"} onChange={(e) => setAnRunOverrides((prev) => ({...prev, [run.key]: e.target.value}))} className={"border rounded px-2 py-1 " + (isDark ? "bg-slate-900 text-slate-100 border-slate-600" : "bg-white text-slate-900 border-slate-300")}>
                      <option value="auto">Unknown / automatic evidence</option><option value="fresh">Known fresh tires</option><option value="race">Include as comparable race run</option><option value="used">Known same used tires</option><option value="exclude">Exclude this run</option>
                    </select>
                  </div>)}
                </details>)}</div>
              </>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Self-contained comparison chart. Helpers are scoped to avoid page-name collisions.
const PracticeOverlayChart = (() => {
const PALETTE = [
  "#60a5fa", // blue
  "#f59e0b", // orange
  "#4ade80", // green
  "#f87171", // red
  "#22d3ee", // cyan
  "#e879f9", // magenta
  "#facc15", // yellow
  "#a78bfa", // violet
  "#fb7185", // rose
  "#2dd4bf", // teal
];

const MIN_LAPS = 3;
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const num = (value) => {
  if (value === null || value === undefined) return NaN;
  const text = String(value).trim().replace(/,/g, "");
  if (!text || text === "-" || text === "—" || /^n\/?a$/i.test(text)) return NaN;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : NaN;
};
const avg = (values) => {
  const usable = values.filter(Number.isFinite);
  return usable.length
    ? usable.reduce((sum, value) => sum + value, 0) / usable.length
    : NaN;
};
const percentile = (values, p) => {
  const usable = values.filter(Number.isFinite).slice().sort((a, b) => a - b);
  if (!usable.length) return NaN;
  const index = (usable.length - 1) * clamp(p, 0, 1);
  const lo = Math.floor(index);
  const hi = Math.ceil(index);
  if (lo === hi) return usable[lo];
  const t = index - lo;
  return usable[lo] * (1 - t) + usable[hi] * t;
};
const median = (values) => percentile(values, 0.5);
const stdDev = (values) => {
  const usable = values.filter(Number.isFinite);
  if (usable.length < 2) return NaN;
  const mean = avg(usable);
  const variance =
    usable.reduce((sum, value) => sum + (value - mean) ** 2, 0) /
    (usable.length - 1);
  return Math.sqrt(variance);
};
const inferDriverKey = (row) =>
  Object.keys(row || {}).find((key) => /^driver\b/i.test(key)) || "Driver";
const numericLapKeys = (row, maxLap) =>
  Object.keys(row || {})
    .filter((key) => /^\d+$/.test(String(key)))
    .map(Number)
    .filter((lap) => !Number.isFinite(maxLap) || lap <= maxLap)
    .sort((a, b) => a - b);
const formatLap = (value, digits = 2) =>
  Number.isFinite(value) ? `${value.toFixed(digits)}s` : "—";
const formatDelta = (value, digits = 3) => {
  if (!Number.isFinite(value)) return "—";
  if (Math.abs(value) < 0.0005) return "0.000";
  return `${value > 0 ? "+" : ""}${value.toFixed(digits)}`;
};
const contiguousWindows = (points, window) => {
  if (points.length < window) return [];
  const windows = [];
  for (let i = 0; i <= points.length - window; i += 1) {
    const slice = points.slice(i, i + window);
    const consecutive = slice.every(
      (point, index) => index === 0 || point.lap === slice[index - 1].lap + 1
    );
    if (consecutive) windows.push(slice);
  }
  return windows;
};
const bestConsecutiveAverage = (points, window, key = "time") => {
  const values = contiguousWindows(points, window)
    .map((slice) => avg(slice.map((point) => point[key])))
    .filter(Number.isFinite);
  return values.length ? Math.min(...values) : NaN;
};
const lastConsecutiveAverage = (points, window, key = "time") => {
  const windows = contiguousWindows(points, window);
  if (!windows.length) return NaN;
  const value = avg(windows[windows.length - 1].map((point) => point[key]));
  return Number.isFinite(value) ? value : NaN;
};
  // Use the complete field for the benchmark, regardless of visible selections.
  function analyzeField(sourceRows, driverKey, maxLap, maxLapTime, trim, dark = true, lapStart = 1, lapEnd = Infinity) {
    const cleaned = [];
    for (const row of sourceRows) {
      const name = String(row?.[driverKey] ?? "").trim();
      if (!name || cleaned.some((s) => s.name === name)) continue;
      let points = numericLapKeys(row, maxLap)
        .map((lap) => ({ lap, time: num(row[String(lap)]) }))
        .filter((p) => Number.isFinite(p.time) && p.time > 0 &&
          (!Number.isFinite(maxLapTime) || p.time <= maxLapTime));
      const originalPoints = points.slice();
      if (trim && points.length >= MIN_LAPS) {
        const middle = median(points.map((p) => p.time));
        const mad = median(points.map((p) => Math.abs(p.time - middle)));
        const band = Math.max(4 * 1.4826 * mad, middle * 0.015);
        points = points.filter((p) => Math.abs(p.time - middle) <= band);
      }
      points = points.filter((p) => p.lap >= lapStart && p.lap <= lapEnd);
      const originalCount = originalPoints.filter((p) => p.lap >= lapStart && p.lap <= lapEnd).length;
      if (!points.length) continue;
      cleaned.push({ name, points, removed: originalCount - points.length });
    }
    const field = new Map();
    for (const s of cleaned) for (const p of s.points) {
      if (!field.has(p.lap)) field.set(p.lap, []);
      field.get(p.lap).push(p.time);
    }
    const baseline = new Map([...field].map(([lap, values]) => [lap, median(values)]));
    return cleaned.sort((a, b) => a.name.localeCompare(b.name)).map((s, i) => {
      const points = s.points.map((p) => ({ ...p, delta: p.time - baseline.get(p.lap) }));
      const times = points.map((p) => p.time);
      const segments = [];
      for (const point of points) {
        const last = segments[segments.length - 1];
        if (!last || point.lap !== last[last.length - 1].lap + 1) segments.push([point]);
        else last.push(point);
      }
      const longest = segments.slice().sort((a, b) => b.length - a.length)[0] || [];
      const falloff = longest.length >= 10 ? avg(longest.slice(-5).map((p) => p.time)) - avg(longest.slice(0, 5).map((p) => p.time)) : NaN;
      return { ...s, points, id: `driver_${i}`, color: i < PALETTE.length ? (dark ? PALETTE[i] : ["#2563eb", "#b45309", "#15803d", "#dc2626", "#0891b2", "#a21caf", "#a16207", "#7c3aed", "#be123c", "#0f766e"][i]) : `hsl(${(i * 137.508) % 360}, 68%, ${dark ? 62 : 38}%)`,
        stats: { laps: points.length, removed: s.removed, falloff, average: avg(times), best: Math.min(...times),
          best5: bestConsecutiveAverage(points, 5), best10: bestConsecutiveAverage(points, 10),
          best15: bestConsecutiveAverage(points, 15), stability: stdDev(times),
          vsMedian: avg(points.map((p) => p.delta)) } };
    });
  }
  const shortName = (name) => {
    const parts = name.trim().split(/\s+/);
    return parts.length > 1 ? `${parts[0][0]}. ${parts.slice(1).join(" ")}` : name;
  };
  return function PracticeOverlayChart({ rows = [], allRows, driverKey: suppliedKey,
    maxLap = Infinity, maxLapTime, isDark = true, resetKey, onSelectionChange }) {
    const sourceRows = allRows || rows;
    const driverKey = suppliedKey || inferDriverKey(sourceRows[0]);
    const [selected, setSelected] = useState([]);
    const [display, setDisplay] = useState("all");
    const [mode, setMode] = useState("raw");
    const [focused, setFocused] = useState(null);
    const [query, setQuery] = useState("");
    const [trim, setTrim] = useState(true);
    const [showStats, setShowStats] = useState(false);
    const [rolling, setRolling] = useState(1);
    const [lapStart, setLapStart] = useState(1);
    const [lapEnd, setLapEnd] = useState(null);
    const availableMax = useMemo(() => Math.max(1, ...sourceRows.flatMap((row) => numericLapKeys(row, maxLap))), [sourceRows, maxLap]);
    const start = Math.min(lapStart, availableMax);
    const end = Math.max(start, Math.min(lapEnd ?? availableMax, availableMax));
    const [mobile, setMobile] = useState(false);
    useEffect(() => {
      const resize = () => setMobile(window.innerWidth < 700);
      resize(); window.addEventListener("resize", resize);
      return () => window.removeEventListener("resize", resize);
    }, []);
    const selectionKey = JSON.stringify(rows.map((r) => String(r?.[driverKey] ?? "").trim()).filter(Boolean).sort());
    useEffect(() => {
      const names = JSON.parse(selectionKey);
      setSelected(names); setDisplay(names.length ? "compare" : "all"); setFocused(null);
    }, [selectionKey]);
    useEffect(() => {
      setMode("raw"); setQuery(""); setFocused(null); setTrim(true); setShowStats(false); setRolling(1); setLapStart(1); setLapEnd(null);
    }, [resetKey]);
    const directoryField = useMemo(() => analyzeField(sourceRows, driverKey, maxLap, maxLapTime, trim, isDark), [sourceRows, driverKey, maxLap, maxLapTime, trim, isDark]);
    const field = useMemo(() => analyzeField(sourceRows, driverKey, maxLap, maxLapTime, trim, isDark, start, end).map((driver) => ({ ...driver, color: directoryField.find((item) => item.name === driver.name)?.color || driver.color })),
      [sourceRows, driverKey, maxLap, maxLapTime, trim, isDark, start, end, directoryField]);
    const visible = useMemo(() => display === "all" ? field : field.filter((s) => selected.includes(s.name)), [field, display, selected]);
    const activeFocus = visible.some((s) => s.name === focused) ? focused : null;
    const detailed = visible.length > 0 && visible.length <= 6;
    const plotted = useMemo(() => visible.map((s) => ({ ...s, plotPoints: s.points.map((p, index) => {
      const slice = s.points.slice(Math.max(0, index - rolling + 1), index + 1);
      const contiguous = slice.length === rolling && slice.every((v, j) => j === 0 || v.lap === slice[j - 1].lap + 1);
      return { ...p, value: contiguous ? avg(slice.map((v) => mode === "raw" ? v.time : v.delta)) : null };
    }) })), [visible, rolling, mode]);
    const sorted = useMemo(() => [...visible].sort((a, b) => a.stats.average - b.stats.average), [visible]);
    const data = useMemo(() => {
      const laps = visible.flatMap((s) => s.points.map((p) => p.lap));
      if (!laps.length) return [];
      const byLap = new Map();
      // Explicit empty laps prevent lines from bridging missing or removed laps.
      for (let lap = Math.min(...laps); lap <= Math.max(...laps); lap++) {
        byLap.set(lap, Object.fromEntries([["lap", lap], ...visible.map((s) => [s.id, null])]));
      }
      for (const s of plotted) for (const p of s.plotPoints) byLap.get(p.lap)[s.id] = p.value;
      return [...byLap.values()];
    }, [visible, plotted]);
    const domain = useMemo(() => {
      const values = plotted.flatMap((s) => s.plotPoints.map((p) => p.value)).filter(Number.isFinite);
      if (!values.length) return [0, 1];
      let lo = Math.min(...values), hi = Math.max(...values);
      if (mode === "median") { lo = Math.min(lo, 0); hi = Math.max(hi, 0); }
      const pad = Math.max((hi - lo) * 0.09, 0.04);
      return [lo - pad, hi + pad];
    }, [plotted, mode]);
    const changeSelection = (names) => {
      setSelected(names); setFocused(null);
      if (onSelectionChange) onSelectionChange(names);
    };
    const toggleDriver = (name) => {
      setDisplay("compare");
      changeSelection(selected.includes(name) ? selected.filter((n) => n !== name) : [...selected, name]);
    };
    const pin = (name) => setFocused((prev) => prev === name ? null : name);
    const t = { text: isDark ? "#f4f4f5" : "#18181b", muted: isDark ? "#a1a1aa" : "#52525b",
      panel: isDark ? "#0f172a" : "#ffffff", inner: isDark ? "#020617" : "#f8fafc",
      border: isDark ? "#1e293b" : "#e2e8f0", accent: isDark ? "#7dd3fc" : "#0369a1",
      green: isDark ? "#6ee7b7" : "#047857", red: isDark ? "#fda4af" : "#be123c" };
    const button = (active = false) => ({ border: `1px solid ${active ? t.accent : t.border}`,
      borderRadius: 8, padding: "8px 12px", background: active ? (isDark ? "#123b4a" : "#e0f2fe") : t.inner,
      color: active ? t.accent : t.muted, fontSize: 12, fontWeight: 650, cursor: "pointer", minHeight: 36 });
    const leaders = [["Fastest average", "average"], ["Best 5 consecutive", "best5"], ["Best 10 consecutive", "best10"]].map(([label, key]) => ({
      label, key, driver: [...visible].filter((s) => Number.isFinite(s.stats[key])).sort((a, b) => a.stats[key] - b.stats[key])[0] }));
    const matching = directoryField.filter((s) => s.name.toLowerCase().includes(query.toLowerCase()));
    const formatValue = (v) => mode === "raw" ? formatLap(v, 3) : `${formatDelta(v)}s`;
    const tooltip = ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const values = payload.filter((p) => Number.isFinite(p.value)).sort((a, b) => a.value - b.value);
      const shown = activeFocus ? values.filter((p) => p.name === activeFocus) : values.slice(0, 8);
      const fastest = values[0]?.value;
      return <div style={{ background: t.panel, border: `1px solid ${t.border}`, color: t.text, padding: 14,
        borderRadius: 12, boxShadow: "0 12px 36px #0003", width: mobile ? 255 : detailed ? 360 : 275, maxWidth: "80vw" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10, fontSize: 12, color: t.muted }}>
          <span>Session lap <strong style={{ color: t.text }}>{label}</strong></span><span>{rolling > 1 ? `${rolling}-lap average` : mode === "raw" ? "Lap time" : "Vs field"}</span>
        </div>
        {shown.map((p) => <div key={p.dataKey} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", fontSize: 12 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: p.color, flexShrink: 0 }} />
          <div style={{ flex: 1 }}><span>{values.indexOf(p) + 1}. {shortName(p.name)}</span>
            {detailed && <div style={{ color: t.muted, fontSize: 10, marginTop: 4 }}>Gap {formatDelta(p.value - fastest)}s · Lap {formatLap(visible.find((s) => s.name === p.name)?.points.find((v) => v.lap === Number(label))?.time, 3)}</div>}</div>
          <strong style={{ fontVariantNumeric: "tabular-nums", color: mode === "median" ? (p.value <= 0 ? t.green : t.red) : t.text }}>{formatValue(p.value)}</strong>
        </div>)}
        {!activeFocus && values.length > 8 && <div style={{ fontSize: 11, color: t.muted, marginTop: 8 }}>Fastest 8 of {values.length} · Pin a driver for detail</div>}
      </div>;
    };
    if (!sourceRows.length) return null;
    return <section aria-label="Practice comparison chart" style={{ background: t.panel, color: t.text, border: `1px solid ${t.border}`,
      borderRadius: 16, padding: mobile ? 12 : 20, minWidth: 0, boxShadow: "0 4px 20px #0000000a" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "center", marginBottom: 18 }}>
        <div><div style={{ color: t.accent, fontSize: 10, fontWeight: 700, letterSpacing: ".12em", marginBottom: 5 }}>PRACTICE PACE</div>
          <h3 style={{ fontSize: 20, fontWeight: 750, margin: 0 }}>Lap comparison</h3>
          <p style={{ margin: "5px 0 0", color: t.muted, fontSize: 12 }}>{detailed ? "Detailed comparison · Up to six drivers" : "Follow the full field or select up to six drivers for detail."}</p></div>
        <div role="group" aria-label="Visible drivers" style={{ display: "flex", gap: 6 }}>
          {[["all", "Full field"], ["compare", `Selected (${selected.length})`]].map(([key, label]) => <button key={key} type="button" aria-pressed={display === key} style={button(display === key)} onClick={() => { setDisplay(key); setFocused(null); }}>{label}</button>)}
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", paddingBottom: 16 }}>
        <div role="group" aria-label="Chart metric" style={{ display: "flex", gap: 6 }}>
          {[["raw", "Lap time"], ["median", "Vs field median"]].map(([key, label]) => <button key={key} type="button" aria-pressed={mode === key} style={button(mode === key)} onClick={() => setMode(key)}>{label}</button>)}
        </div>
        <label style={{ fontSize: 12, color: t.muted, display: "flex", gap: 7, alignItems: "center", cursor: "pointer" }}>
          <input type="checkbox" checked={trim} onChange={(e) => setTrim(e.target.checked)} style={{ accentColor: t.accent }} />Filter outlier laps
        </label>
      </div>
      <div style={{ background: t.inner, border: `1px solid ${t.border}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
          <strong style={{ fontSize: 12 }}>Lap window · Everyone</strong>
          <span style={{ color: t.muted, fontSize: 11 }}>Chart, rolling averages, and statistics use laps {start}–{end}.</span>
          <button type="button" style={{ ...button(), marginLeft: "auto", padding: "4px 9px", minHeight: 28 }} onClick={() => { setLapStart(1); setLapEnd(null); }}>All laps</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 16 }}>
          <label style={{ fontSize: 11, color: t.muted }}>From
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <input aria-label="Start lap slider" type="range" min={1} max={availableMax} value={start} onChange={(e) => setLapStart(Math.min(Number(e.target.value), end))} style={{ flex: 1, minWidth: 0, accentColor: t.accent }} />
              <input aria-label="Start lap" type="number" min={1} max={end} value={start} onChange={(e) => setLapStart(Math.max(1, Math.min(Number(e.target.value) || 1, end)))} style={{ ...button(), width: 70, boxSizing: "border-box", color: t.text }} />
            </div>
          </label>
          <label style={{ fontSize: 11, color: t.muted }}>Through
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <input aria-label="End lap slider" type="range" min={1} max={availableMax} value={end} onChange={(e) => setLapEnd(Math.max(start, Number(e.target.value)))} style={{ flex: 1, minWidth: 0, accentColor: t.accent }} />
              <input aria-label="End lap" type="number" min={start} max={availableMax} value={end} onChange={(e) => setLapEnd(Math.max(start, Math.min(availableMax, Number(e.target.value) || start)))} style={{ ...button(), width: 70, boxSizing: "border-box", color: t.text }} />
            </div>
          </label>
        </div>
      </div>
      <div role="group" aria-label="Lap averaging" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {[[1, "Every lap"], [5, "Rolling 5"], [10, "Rolling 10"]].map(([value, label]) => <button type="button" key={value} style={button(rolling === value)} aria-pressed={rolling === value} onClick={() => setRolling(value)}>{label}</button>)}
        <span style={{ fontSize: 11, color: t.muted, alignSelf: "center", marginLeft: 4 }}>Consecutive laps only · Same window for all drivers</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "repeat(3,minmax(0,1fr))", gap: 10, marginBottom: 16 }}>
        {leaders.map(({ label, key, driver }) => <div key={key} style={{ border: `1px solid ${t.border}`, borderRadius: 10, padding: "12px 14px", background: t.inner }}>
          <div style={{ fontSize: 11, color: t.muted, marginBottom: 7 }}>{label}</div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 650 }}>{driver ? shortName(driver.name) : "—"}</span>
            <strong style={{ fontSize: 20, fontVariantNumeric: "tabular-nums", letterSpacing: "-.03em", color: t.accent }}>{formatLap(driver?.stats[key], 3)}</strong>
          </div>
        </div>)}
      </div>
      <div style={{ background: t.inner, border: `1px solid ${t.border}`, borderRadius: 12, padding: mobile ? "12px 2px 4px" : "14px 12px 6px" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "0 12px 8px", fontSize: 11, color: t.muted }}>
          <span>{visible.length} drivers · {mode === "raw" ? "Seconds · Lower is faster" : "Seconds from full-field median · Negative is faster"}</span>
          {activeFocus && <button type="button" onClick={() => setFocused(null)} style={{ ...button(true), padding: "4px 8px", minHeight: 28, marginLeft: "auto" }}>{shortName(activeFocus)} · Clear focus ×</button>}
        </div>
        {visible.length ? <div style={{ width: "100%", height: mobile ? 380 : detailed ? 500 : 440 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 12, right: detailed && !mobile ? 100 : 20, bottom: 22, left: 0 }}>
              <CartesianGrid vertical={false} stroke={t.border} strokeDasharray="3 6" />
              <XAxis dataKey="lap" type="number" domain={[start, Math.max(start + 1, end)]} allowDecimals={false}
                tickLine={false} axisLine={false} minTickGap={30} tick={{ fill: t.muted, fontSize: 11 }}
                label={{ value: "Session lap", position: "insideBottom", offset: -14, fill: t.muted, fontSize: 11 }} />
              <YAxis domain={domain} width={mobile ? 54 : 66} tickLine={false} axisLine={false} tickCount={6}
                tick={{ fill: t.muted, fontSize: 11 }} tickFormatter={(v) => mode === "raw" ? Number(v).toFixed(2) : formatDelta(v, 2)} />
              {mode === "median" && <ReferenceLine y={0} stroke={t.muted} strokeDasharray="5 5" />}
              <Tooltip content={tooltip} cursor={{ stroke: t.muted, strokeDasharray: "4 4" }} wrapperStyle={{ zIndex: 30 }} />
              {[...plotted].sort((a, b) => Number(a.name === activeFocus) - Number(b.name === activeFocus)).map((s) => {
                const pinned = s.name === activeFocus, dim = activeFocus && !pinned;
                return <Line key={s.id} dataKey={s.id} name={s.name} type="linear" stroke={s.color}
                  strokeWidth={pinned ? 3.4 : detailed ? 2.7 : display === "all" ? 1.5 : 2.3} strokeOpacity={dim ? 0.12 : display === "all" && !pinned ? 0.55 : 1}
                  label={detailed && !mobile ? ({ x, y, index }) => {
                    const last = [...data].reverse().find((row) => Number.isFinite(row[s.id]));
                    if (!last || data[index]?.lap !== last.lap || !Number.isFinite(y)) return null;
                    return <text x={x + 8} y={y + 4} fill={s.color} fontSize={10} fontWeight={650}>{shortName(s.name)}</text>;
                  } : false}
                  dot={detailed || pinned ? ({ cx, cy, index }) => {
                    if (!Number.isFinite(cx) || !Number.isFinite(cy) || !Number.isFinite(data[index]?.[s.id])) return <g key={index} />;
                    const best = rolling === 1 && mode === "raw" && data[index][s.id] === s.stats.best;
                    return <circle key={index} cx={cx} cy={cy} r={best ? 5 : detailed ? 2.8 : 2} fill={s.color} stroke={best ? t.text : t.panel} strokeWidth={best ? 1.5 : 1}><title>{best ? "Best lap · " : ""}{s.name}: {formatValue(data[index][s.id])}</title></circle>;
                  } : false}
                  activeDot={dim ? false : { r: 5, stroke: t.panel, strokeWidth: 2, onClick: () => pin(s.name) }}
                  connectNulls={false} isAnimationActive={false} />;
              })}
            </LineChart>
          </ResponsiveContainer>
        </div> : <div style={{ padding: "70px 20px", textAlign: "center" }}>
          <div style={{ fontWeight: 650, marginBottom: 8 }}>No drivers to compare</div>
          <div style={{ fontSize: 12, color: t.muted }}>Select drivers below or in the lap table, or widen the lap window.</div>
          <button type="button" style={{ ...button(true), marginTop: 16 }} onClick={() => setDisplay("all")}>Show full field</button>
        </div>}
      </div>
      {rolling > 1 && visible.length > 0 && !plotted.some((s) => s.plotPoints.some((p) => Number.isFinite(p.value))) && <div role="status" style={{ color: t.muted, fontSize: 12, padding: "12px 0" }}>No {rolling}-lap consecutive stretch in this window. Widen the window or choose Every lap.</div>}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, margin: "16px 0 10px" }}>
        <input type="search" aria-label="Find a driver in the chart" placeholder="Find a driver…" value={query} onChange={(e) => setQuery(e.target.value)}
          style={{ ...button(), cursor: "text", color: t.text, width: mobile ? "100%" : 220, boxSizing: "border-box" }} />
        <span style={{ fontSize: 11, color: t.muted }}>{display === "all" ? "Click a driver to focus their line" : "Click to select · Target button to focus"}</span>
        {display === "compare" && selected.length > 0 && <button type="button" onClick={() => changeSelection([])} style={{ ...button(), marginLeft: "auto" }}>Clear selection</button>}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7, maxHeight: 152, overflowY: "auto", padding: "3px 2px 6px" }}>
        {matching.map((s) => {
          const chosen = selected.includes(s.name), pinned = s.name === activeFocus;
          return <div key={s.id} style={{ display: "inline-flex", alignItems: "stretch" }}>
            <button type="button" title={s.name} aria-pressed={display === "all" ? pinned : chosen}
              onClick={() => display === "all" ? pin(s.name) : toggleDriver(s.name)}
              style={{ ...button(display === "all" ? pinned : chosen), display: "inline-flex", gap: 7, alignItems: "center", padding: "7px 10px",
                opacity: display === "all" && activeFocus && !pinned ? 0.55 : 1, borderRadius: display === "compare" && chosen ? "8px 0 0 8px" : 8 }}>
              <span style={{ background: s.color, width: 8, height: 8, borderRadius: 4 }} />{shortName(s.name)}
            </button>
            {display === "compare" && chosen && <button type="button" title={`Focus ${s.name}`} aria-label={`Focus ${s.name}`} aria-pressed={pinned}
              onClick={() => pin(s.name)} style={{ ...button(pinned), borderRadius: "0 8px 8px 0", marginLeft: -1, padding: "7px 9px" }}>◎</button>}
          </div>;
        })}
        {!matching.length && <span style={{ color: t.muted, fontSize: 12, padding: 8 }}>No matching drivers with usable laps.</span>}
      </div>
      <div style={{ fontSize: 11, color: t.muted, lineHeight: 1.6, marginTop: 8 }}>
        {trim ? "Outliers filtered per driver. " : "Showing all valid recorded laps. "}Gaps indicate missing or removed laps. Field median compares the same session lap across all eligible drivers; tire age can differ.
      </div>
      {detailed && <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : `repeat(${Math.min(3, sorted.length)},minmax(0,1fr))`, gap: 10, marginTop: 16 }}>
        {sorted.map((s) => <div key={s.id} style={{ background: t.inner, border: `1px solid ${t.border}`, borderTop: `3px solid ${s.color}`, borderRadius: 10, padding: 14 }}>
          <button type="button" onClick={() => pin(s.name)} style={{ border: 0, padding: 0, background: "transparent", color: t.text, cursor: "pointer", fontSize: 13, fontWeight: 700 }}>{s.name}</button>
          <div style={{ fontSize: 11, color: t.muted, marginTop: 5 }}>{s.stats.laps} usable laps · {s.stats.removed} filtered · Laps {start}–{end}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px", marginTop: 14 }}>
            {[["Average", formatLap(s.stats.average, 3)], ["Best lap", formatLap(s.stats.best, 3)], ["Best 5", formatLap(s.stats.best5, 3)], ["Best 10", formatLap(s.stats.best10, 3)], ["Best 15", formatLap(s.stats.best15, 3)], ["Falloff¹", Number.isFinite(s.stats.falloff) ? `${formatDelta(s.stats.falloff)}s` : "—"], ["Std dev", Number.isFinite(s.stats.stability) ? `${s.stats.stability.toFixed(3)}s` : "—"], ["Vs field", `${formatDelta(s.stats.vsMedian)}s`]].map(([label, value]) => <div key={label}><div style={{ fontSize: 10, color: t.muted, marginBottom: 4 }}>{label}</div><strong style={{ fontSize: 14, fontVariantNumeric: "tabular-nums" }}>{value}</strong></div>)}
          </div>
        </div>)}
        <div style={{ gridColumn: "1 / -1", color: t.muted, fontSize: 11, lineHeight: 1.6 }}>¹ Falloff = last 5 minus first 5 in the longest continuous recorded segment within the window (10+ laps). Positive means slower. Tire changes are not identified. Larger outlined dots mark each driver's best lap in Every lap / Lap time mode.</div>
      </div>}
      <button type="button" onClick={() => setShowStats(!showStats)} aria-expanded={showStats} style={{ ...button(showStats), marginTop: 14 }}>{showStats ? "Hide" : "Show"} pace summary</button>
      {showStats && <div style={{ overflowX: "auto", marginTop: 12, border: `1px solid ${t.border}`, borderRadius: 10 }}>
        <table style={{ width: "100%", minWidth: 800, borderCollapse: "collapse", fontSize: 12, fontVariantNumeric: "tabular-nums" }}>
          <thead><tr style={{ background: t.inner, color: t.muted }}>{["Driver", "Laps", "Average", "Best lap", "Best 5", "Best 10", "Best 15", "Std dev", "Vs field"].map((h) => <th key={h} style={{ padding: "11px 12px", textAlign: h === "Driver" ? "left" : "right", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>)}</tr></thead>
          <tbody>{sorted.map((s) => <tr key={s.id} style={{ borderTop: `1px solid ${t.border}`, background: s.name === activeFocus ? t.inner : "transparent" }}>
            <td style={{ padding: "11px 12px", whiteSpace: "nowrap" }}><button type="button" onClick={() => pin(s.name)} style={{ background: "none", border: 0, padding: 0, color: t.text, cursor: "pointer", fontWeight: 650 }}><span style={{ color: s.color, marginRight: 8 }}>●</span>{s.name}</button></td>
            {[s.stats.laps, formatLap(s.stats.average, 3), formatLap(s.stats.best, 3), formatLap(s.stats.best5, 3), formatLap(s.stats.best10, 3), formatLap(s.stats.best15, 3), Number.isFinite(s.stats.stability) ? s.stats.stability.toFixed(3) : "—", formatDelta(s.stats.vsMedian)].map((v, i) => <td key={i} style={{ padding: "11px 12px", textAlign: "right", color: i === 7 ? (s.stats.vsMedian <= 0 ? t.green : t.red) : t.text }}>{v}</td>)}
          </tr>)}</tbody>
        </table>
        <div style={{ color: t.muted, fontSize: 11, padding: 12 }}>Best 5/10/15 require consecutive recorded laps. Standard deviation includes tire falloff and run differences.</div>
      </div>}
    </section>;
  };

})();
